// Session: one run from intro to result screen (TECH_SPEC §4.1/§4.3).
//
// Two Sim instances advance in lockstep: the player's, driven by live input,
// and the ghost's, driven by the opponent's recorded input stream. Because
// forward speed is a pure function of the frame number, a living ghost is
// always level with the player — the meaningful gap is against the opponent's
// *final* score, which is what the HUD meter and the overtake beat use.

import {
  Action,
  FQ_ONE,
  MAX_EVENTS_PER_FRAME,
  MAX_FRAMES,
  MAX_INPUT_EVENTS,
  FQ_PER_METER,
  Sim,
  metersFromFQ,
  type InputEvent,
  type Obstacle,
  type PauseEvent,
} from '@beatme/sim';

import {
  ApiError,
  cachePendingRun,
  ensureDeviceId,
  fetchDailyRival,
  fetchSelfBest,
  markPlayed,
  submitRun,
} from '../api';
import { track as trackEvent, type GameEndProps } from '../analytics';
import { bestLine, readBest, reconcileBest, recordVerifiedBest } from '../best';
import { DEMO_NICKNAME, createPlaytest, type DebugOptions, type Playtest } from '../debug';
import {
  duelCountdownMs,
  duelGapM,
  duelInputBudget,
  duelLinePool,
  duelMarginFrames,
  duelMarginM,
  duelOutcomeFor,
  duelParentRunId,
  duelSeals,
  duelStampPool,
  handoffEligible,
  isHandoff,
  deathStampPool,
  resultSlots,
  sealVerb,
  type DuelOutcome,
  type DuelSlice,
  type SlotKind,
} from '../duel';
import { cardVariantFor, shareUrlFor } from '../experiment';
import { ladderRungs, rungsAvailable, type Rung } from '../ladder';
import { classifySubmitFailure } from '../submitOutcome';
import {
  BAR_BOTTOM_FQ,
  DUEL_SEAL_HOLD_MS,
  DUEL_SEAL_SLOWMO_FRAMES,
  DUEL_SEAL_TIMESCALE,
  FLASH_FRAMES,
  FLOATING_TEXT_FRAMES,
  HITBOX_SLIDE_H,
  HITBOX_SLIDE_W,
  HITBOX_STAND_H,
  HITBOX_STAND_W,
  NEAR_MISS_FQ,
  NEAR_MISS_SLOWMO_FRAMES,
  NEAR_MISS_TIMESCALE,
  OVERTAKE_SLOWMO_FRAMES,
  OVERTAKE_TIMESCALE,
  REVIVE_OFFER_SECONDS,
  SPIKE_TOP_FQ,
} from '../config';
import { getLocale, pick, t, type PoolKey } from '../i18n';
import {
  clampStampName,
  peakCoverageFor,
  peakCtaPool,
  peakFor,
  shouldShowSeal,
  stampShrinks,
  type PeakCoverage,
  type PeakKind,
} from '../peak';
import { hashString, mulberry32 } from '../rng';
import type {
  EntryType,
  HandoffWire,
  Opponent,
  Outcome,
  SubmitRunBody,
  SubmitRunResponse,
  Track,
} from '../types';
import { showDuelIntro, type DuelIntroHandle } from './duelIntro';
import { showIntro } from '../ui/intro';
import { showPause } from '../ui/pause';
import { PeakOverlay } from '../ui/peak';
import { ResultView, type LadderRow, type ResultButton } from '../ui/result';
import { showReviveOffer } from '../ui/revive';
import { shareRun } from '../ui/share';
import { showTutorialStep, type TutorialOverlay } from '../ui/tutorial';
import { ads } from './ads';
import { audio } from './audio';
import { InputController } from './input';
import { GameLoop } from './loop';
import { reviveOffer } from './revive';
import {
  Tutorial,
  actionForStep,
  armTutorialNext,
  tutorialArmed,
  type TutorialStep,
} from './tutorial';
import {
  collidingObstacle,
  obstacleKillBox,
  playerKillBox,
  type Rect as GeomRect,
} from './geometry';
import {
  LOGICAL_W,
  PLAYER_LANE,
  Renderer,
  paletteFor,
  type ActorSnapshot,
  type FloatingText,
  type Scene,
} from './render';

export interface RunContext {
  track: Track;
  opponent: Opponent;
  entryType: EntryType;
  /** Only link entries have a parent (design §3.2). */
  parentRunId?: string;
  /** Linked run was flagged → ghost swapped for the official one. */
  flagged: boolean;
  /** Device has never finished a run before → newbie free revive is available. */
  firstEver: boolean;
  /** This device is in the rival-ladder arm (§4bis.21). False = dormant: no
   *  ladder DOM, no ladder requests, result screen unchanged. */
  ladder: boolean;
  /** This device is in the peak-moment arm (§4bis.22). False = dormant: no
   *  overtake amplification, no seal overlay, rendering pixel-identical to the
   *  shipped build. Coverage props on `result_view` ship in BOTH arms. */
  peak: boolean;
  /** This device is in the honest-silhouettes arm (§4bis.23). False = dormant:
   *  legacy spike/anchor drawing, no camera comp, no death-frame flash —
   *  rendering pixel-identical to the shipped build. */
  fair: boolean;
  /**
   * The duel slice this run plays (§4bis.28), or null for an ordinary full run.
   * Non-null ⇒ both Sims fast-forward to `startFrame`, the run ends at
   * `winFrame`, revives are off, and the tutorial is forced off. Null is the
   * shipped game, unchanged — which is what `DUEL_PCT=0` produces for everyone.
   */
  duel: DuelSlice | null;
  /**
   * §4bis.29: the boot-delivery token the server handed back when it actually
   * served this run. Echoed on a duel submit as the proof that this device was
   * served the run it is citing — the basis for chain attachment, replacing the
   * `link_open` analytics row §4bis.28 read (which `/api/events` drops for an
   * absent user-agent, so privacy browsers silently never attached).
   * Absent costs attachment and nothing else: the run still verifies and
   * stores.
   */
  deliveryToken?: string;
  /** This device is in the duel arm. Reported on every event as `duelArm`,
   *  including on the full runs an armed device still plays — T4's guardrail
   *  is exactly that ratio. */
  duelArm: boolean;
  /**
   * §4bis.31: this device is in the 帮我 arm (`HANDOFF_PCT`, salt `handoff-v1`),
   * NESTED inside `duelArm`. False = dormant: no seal plant, no second button
   * on the death screen, no `?h=1` ever shared — the death screen is
   * byte-for-byte what 005 shipped. Reported on every event as `handoffArm`,
   * including on challenges, because T2 is a share of the ARM's deaths.
   */
  handoffArm?: boolean;
  /**
   * §4bis.31: present iff THIS run is a hand-back — the link carried `?h=1` and
   * the server minted a hand-back slice. The sole identifier, exactly as
   * `duel_source_run_id IS NOT NULL` is for a duel. Null on a challenge.
   */
  handoff?: HandoffWire | null;
  /**
   * Is this device the person the life was addressed to? Copy only, never a
   * permission: **the card is addressed, the link is open.** No server-side
   * lock, no reservation, no expiry — a third person opening it is another
   * branch, and chains are already trees. T1 (the whole bet) is the rate at
   * which this is true within 24 h.
   */
  handoffAddressed?: boolean;
}

export interface SessionDeps {
  canvas: HTMLCanvasElement;
  overlay: HTMLElement;
  stage: HTMLElement;
}

const EMPTY_EVENTS: InputEvent[] = [];

export class Session {
  private readonly renderer: Renderer;
  private readonly input: InputController;
  private readonly loop: GameLoop;

  private ctx: RunContext;
  private playerSim!: Sim;
  private ghostSim!: Sim;
  private obstacles: readonly Obstacle[] = [];
  private startX = 0;

  private inputs: InputEvent[] = [];
  private pauses: PauseEvent[] = [];
  private ghostInputs: InputEvent[] = [];
  private ghostCursor = 0;

  private playerPrev: ActorSnapshot = blankActor();
  private playerCur: ActorSnapshot = blankActor();
  private ghostPrev: ActorSnapshot = blankActor();
  private ghostCur: ActorSnapshot = blankActor();
  private ghostAlive = true;
  private ghostDeathX: number | null = null;
  private ghostReviveMarks: number[] = [];
  private ghostRevives = 0;

  private floating: FloatingText[] = [];
  /** Line to float on the next beginRun — set by every rematch that starts
   *  without an intro overlay (own chain, ladder rungs 2 and 3), because an
   *  unexplained new ghost reads as a bug on a phone. Null = say nothing. */
  private rerunBanner: string | null = null;
  private flashFrames = 0;
  private nearMissed = new Set<number>();
  private lastGapIndex = -1;
  private overtaken = false;
  private hudVisible = false;
  private running = false;
  private ended = false;
  private newbieReviveUsed = false;
  /** Design §8.1: one ad revive per run, offered at most once. */
  private adReviveUsed = false;
  private revives = 0;

  private pauseStartedAt = 0;
  private pauseFrame = 0;
  private pendingPause = false;

  /** §4bis.23: pose AT death for the fair camera comp ('stand' folds jump in);
   *  null while alive and on every dormant run. */
  private fairDeathPose: 'stand' | 'slide' | null = null;
  /** §4bis.23 death-frame flash: the two kill boxes over the frozen photo.
   *  Non-null only on a fair-arm collision death, until revive/reset.
   *
   *  It animates on the wall clock, which needs the loop to keep *rendering* —
   *  true under a revive offer (`pause`), false once the run ends (`stop`).
   *  `settled` forces the ≥500 ms end state and is set in two places: at arm
   *  time for autoplay (captures must be deterministic), and in `endRun`
   *  (§4bis.27 — the trailing render after `stop()` is the canvas's last
   *  frame, so the end state is the only one a player can actually see). */
  private deathFlash: {
    playerBox: GeomRect;
    obstacleBox: GeomRect;
    start: number;
    settled: boolean;
  } | null = null;

  /** §4bis.28: how many times this device has restarted THIS duel. Attempt 0
   *  may attach to the chain; every retry after it submits without a parent, so
   *  instant-death retries cannot inflate the depth number T3 reads. */
  private duelAttempt = 0;
  /** The countdown overlay while it is holding the handover frame. */
  private duelIntro: DuelIntroHandle | null = null;
  /** This duel's verdict, once the run has ended. */
  private duelOutcome: DuelOutcome | null = null;
  /**
   * §4bis.30 — the mid-run 活过了 seal. True from the frame the duel's line is
   * passed, and it stays true: the run has changed state (defeated opponent
   * lane, grave tick, territory HUD), and ahead is track nobody in this chain
   * has reached. Never set outside a duel, so `DUEL_PCT = 0` renders exactly
   * the shipped frame.
   */
  private duelSealed = false;
  /** The canvas stamp while it is playing. Wall-clock, because the seal's own
   *  slow-mo makes frames the wrong ruler. Cleared by the death flash. */
  private duelSealStamp: { text: string; start: number } | null = null;
  /** §4bis.31: the hand-back's seed, drawn under the seal stamp. Null outside
   *  the handoff arm, which is what keeps `HANDOFF_PCT=0` pixel-identical. */
  private duelSealPlant: string | null = null;
  /** §4bis.31: `handoff_offer` is once per `game_end`, like `result_view` —
   *  the seal and the result screen both make the ask and both record it. */
  private handoffOfferSent = false;
  /** §4bis.31: the 帮我 button is already on the screen, so the submit's answer
   *  must not add a second one. True only when the mount could already tell —
   *  i.e. in a debug build forcing the server's answer. */
  private handoffShown = false;
  /** §4bis.30: this run ended because the input budget ran out, not because the
   *  player died — the honest end, never a phantom death. */
  private duelBudgetExhausted = false;

  /** Stop-frame tutorial (design §2.3), null on every taught device. */
  private tutorial: Tutorial | null = null;
  private tutorialUi: TutorialOverlay | null = null;
  /** Actions produced by a tutorial dismissal, drained into the frozen frame. */
  private injected: Action[] = [];

  /** Layer-2 playtest state (docs/DEV_PLAYTEST.md); always null in a shipped
   *  build, which is why every debug member lives on it and not on Session. */
  private readonly play: Playtest | null;

  private result: ResultView | null = null;
  /** The seal (§4bis.22), mounted instead of the result screen on a peak run;
   *  the result screen is built when it dismisses. Null outside the arm. */
  private peakUi: PeakOverlay | null = null;
  /** `result_view` coverage prop for this run — computed on EVERY run. */
  private peakCoverage: PeakCoverage = 'none';
  /** result_view fires exactly once per game_end: at seal mount on peak runs
   *  (~1 s earlier than the screen), else at result-screen mount. */
  private resultViewSent = false;
  /** Submit-failure note, held while the seal delays the result screen. */
  private submitNote: string | null = null;
  /** One ladder fetch per result screen, reset with the screen. */
  private ladderLoaded = false;
  /** The result screen's sub-lines as a function of the remembered best and the
   *  run's score, kept so a server answer that contradicts that number can
   *  repaint them (B4). */
  private rebuildLines: ((prevBestM: number, scoreM: number) => string[]) | null = null;
  private runSeed = 0;
  private shareCode: string | null = null;
  /** Verified response for the run on screen — the seed of an own-chain rerun. */
  private submitted: SubmitRunResponse | null = null;
  /** Guards render() against a resize landing before the first reset(). */
  private ready = false;

  constructor(
    private readonly deps: SessionDeps,
    ctx: RunContext,
    debug: DebugOptions | null = null,
  ) {
    this.ctx = ctx;
    this.play = __BEATME_DEBUG__ ? createPlaytest(debug) : null;
    this.renderer = new Renderer(deps.canvas);
    this.renderer.setTheme(ctx.track.theme);
    this.applyThemeVars();
    audio.installResumeHooks();
    this.input = new InputController(deps.stage, { onFirstGesture: () => audio.unlock() });
    this.loop = new GameLoop({
      update: () => this.update(),
      render: (alpha) => this.render(alpha),
    });

    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);
    document.addEventListener('visibilitychange', this.onVisibility);

    if (__BEATME_DEBUG__ && this.play) {
      if (this.play.timescale > 1) this.loop.setStepMultiplier(this.play.timescale);
      this.play.expose(() => (this.ready ? this.playerSim.state.frame : 0));
    }
  }

  /** Intro overlay → run. */
  async start(): Promise<void> {
    this.reset();
    this.input.attach();
    this.render(0);

    // Autoplay boots straight into the run: the bot's first frame counts as the
    // start tap, so the intro overlay never gets in the harness's way.
    if (__BEATME_DEBUG__ && this.play?.isAutoplay) {
      this.play.badge(this.deps.overlay);
    } else {
      const choice = await showIntro(this.deps.overlay, {
        entryType: this.ctx.entryType,
        opponentName: this.ctx.opponent.nickname,
        opponentScoreM: this.ctx.opponent.scoreM,
        opponentOfficial: this.ctx.opponent.official === true,
        showSlideHint: this.ctx.firstEver,
        flagged: this.ctx.flagged,
        ...(this.ctx.duel ? { duel: { sourceEndM: this.ctx.duel.sourceEndM } } : {}),
        // §4bis.31: the recipient has to be told they were NAMED. Same screen,
        // same two buttons, same routing — one different question.
        ...(this.ctx.duel && isHandoff(this.ctx.duel)
          ? {
              handoff: {
                fromName: clampStampName(this.ctx.opponent.nickname),
                frontierM: this.ctx.handoff?.frontierM ?? this.ctx.duel.sourceEndM,
                startM: this.ctx.duel.startM,
                toIsYou: this.ctx.handoffAddressed === true,
              },
            }
          : {}),
      });
      // 跑全程: drop the slice and rebuild from frame 0. `reset()` already
      // fast-forwarded for the duel, so the choice has to undo that rather than
      // just skip the countdown.
      if (this.ctx.duel && choice === 'full') {
        this.ctx = { ...this.ctx, duel: null };
        this.reset();
        this.render(0);
      }
    }

    audio.unlock();
    this.beginRun();
  }

  destroy(): void {
    this.loop.stop();
    this.input.detach();
    this.tutorialUi?.remove();
    this.tutorialUi = null;
    this.peakUi?.remove();
    this.peakUi = null;
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('orientationchange', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  // --- setup --------------------------------------------------------------

  private reset(): void {
    const seed = this.ctx.track.seed;
    // Dual-path generation (TECH_SPEC §4bis.24): build the track on the boot
    // payload's persisted simVersion, so an old share link replays and races
    // the obstacles it was recorded on, not the current generator's.
    const gen = this.ctx.track.simVersion;
    this.playerSim = new Sim(seed, undefined, gen);
    this.ghostSim = new Sim(this.ctx.opponent.seed || seed, undefined, gen);
    this.obstacles = this.playerSim.obstacles;
    this.startX = this.playerSim.state.x;

    this.inputs = [];
    this.pauses = [];
    this.ghostInputs = sanitizeGhostInputs(this.ctx.opponent.inputs);
    this.ghostCursor = 0;
    this.duelOutcome = null;
    this.duelSealed = false;
    this.duelSealStamp = null;
    this.duelSealPlant = null;
    this.handoffOfferSent = false;
    this.handoffShown = false;
    this.duelBudgetExhausted = false;
    // A run can END with a slow-mo window still armed — the seal's 42 frames
    // when the player reaches MAX_FRAMES right after passing the line, or any
    // beat inside its window when `endRun` stops the loop. `onPlayerDeath`
    // clears it on the death path (§4bis.23 precedence); this covers the alive
    // paths, so a retry never starts the next duel at 0.35×.
    this.loop.clearSlowMo();
    this.duelIntro?.dismiss();
    this.duelIntro = null;

    // §4bis.28: fast-forward BOTH sims to the handover, silently. `startX` was
    // just read at frame 0 and stays PLAYER_START_X, so the HUD keeps reading
    // absolute track meters (~390+ at the line) and the §4bis.9 gap meter keeps
    // its meaning: it counts up to his grave, then past it.
    if (this.ctx.duel) this.fastForwardToDuel(this.ctx.duel);

    this.playerCur = snapshot(this.playerSim);
    this.playerPrev = { ...this.playerCur };
    this.ghostCur = snapshot(this.ghostSim);
    this.ghostPrev = { ...this.ghostCur };
    this.ghostAlive = true;
    this.ghostDeathX = null;
    this.ghostReviveMarks = [];
    this.ghostRevives = this.ghostSim.state.revivesUsed;

    this.fairDeathPose = null;
    this.deathFlash = null;
    this.renderer.resetFairCam();

    this.floating = [];
    this.flashFrames = 0;
    this.nearMissed.clear();
    this.lastGapIndex = -1;
    this.overtaken = false;
    this.hudVisible = false;
    this.ended = false;
    this.running = false;
    this.newbieReviveUsed = false;
    this.adReviveUsed = false;
    this.revives = 0;
    this.result?.remove();
    this.result = null;
    this.peakUi?.remove();
    this.peakUi = null;
    this.peakCoverage = 'none';
    this.resultViewSent = false;
    this.submitNote = null;
    this.ladderLoaded = false;
    this.rebuildLines = null;
    this.submitted = null;
    this.runSeed = (hashString(`${this.ctx.track.id}:${Date.now()}`) ^ this.ctx.track.seed) >>> 0;
    this.ready = true;

    // Design §2.3: teach on a fresh device (or when the result screen re-armed
    // it), never during autoplay — a bot must never stop, or every Layer-2 shot
    // and every visual baseline would hang on a tutorial overlay.
    this.tutorialUi?.remove();
    this.tutorialUi = null;
    this.injected = [];
    const autoplaying = __BEATME_DEBUG__ && this.play?.isAutoplay === true;
    const forced = __BEATME_DEBUG__ && this.play?.forceTutorial === true;
    // §4bis.28: the tutorial is forced off inside a duel. Its stop frames are
    // derived from frame-0 bot inputs, so on a slice starting at ~1200 they
    // would all be in the past — and any that were not would freeze the run in
    // open ground. The duel borrows the teaching moment (`duel.teach` on the
    // countdown) and never spends it: a duel does NOT set `tutorialDone`, so
    // this device's next FULL run still gets the stop frames.
    this.tutorial =
      !autoplaying && !this.ctx.duel && (forced || tutorialArmed())
        ? new Tutorial(this.obstacles)
        : null;

    if (__BEATME_DEBUG__ && this.play) {
      // Copy pools are picked from `runSeed`; a wall-clock seed would make every
      // result screenshot a different line. Pin it to the track *seed* — the
      // track id carries the ISO week, which would rot baselines every Monday.
      this.runSeed = this.ctx.track.seed >>> 0;
      this.play.reset(seed);
      // A duel skipped the first `startFrame` frames without consuming any bot
      // input, so the bot's cursor has to skip them too (§4bis.28).
      if (this.ctx.duel) this.play.skipTo(this.ctx.duel.startFrame);
    }
  }

  /**
   * Replay the sender's own events up to the handover on BOTH sims — a tight
   * integer `step()` loop, ≤3600 frames, bit-identical to having played it.
   *
   * The player inherits the sender's body because that is exactly the stream
   * the SERVER will rebuild as the prefix; anything else and the local run and
   * its verification would describe two different races. Critically, none of
   * these events enters `this.inputs`: the submit carries the suffix alone.
   */
  private fastForwardToDuel(duel: DuelSlice): void {
    let i = 0;
    for (let f = 0; f < duel.startFrame; f++) {
      const start = i;
      while (i < this.ghostInputs.length && this.ghostInputs[i]!.f === f) i++;
      const events = start === i ? EMPTY_EVENTS : this.ghostInputs.slice(start, i);
      this.playerSim.step(events);
      this.ghostSim.step(events);
      if (this.ghostSim.state.dead || this.ghostSim.state.finished) break;
    }
    // The ghost carries on from here with the rest of the sender's stream: it
    // is ALIVE through the whole approach and dies in the middle of the slice.
    // That death is the climax, and the answer to "a ghost in a slice is a
    // corpse racing from beyond the grave".
    this.ghostCursor = i;
    this.ghostRevives = this.ghostSim.state.revivesUsed;
  }

  /**
   * §4bis.28's first second: hold the frozen handover frame under the count.
   * Autoplay bypasses it entirely — the §4bis.15 tutorial rule, and for the
   * same reason: a bot must never stop, or every Layer-3 baseline would hang on
   * the overlay. `duel_start` fires when the beat ends, i.e. when a duel was
   * both offered and accepted.
   */
  private beginRun(): void {
    const duel = this.ctx.duel;
    const autoplaying = __BEATME_DEBUG__ && this.play?.isAutoplay === true;
    if (duel && !autoplaying) {
      this.hudVisible = true;
      this.input.setEnabled(false);
      // Paint the handover frame under the overlay: obstacles ahead, both
      // runners on the line. Fairness is solved by preview.
      this.render(0);
      // A device that has never been taught reads the gesture legend during a
      // longer count. `tutorialArmed()` is the same storage flag the stop-frame
      // tutorial keys on, so "untaught" means one thing in both places.
      const untaught = this.ctx.firstEver || tutorialArmed();
      // `&duelhold=1`: stand still on the first beat so the Layer-3 shot has a
      // deterministic frame (debug builds only — a 1.8 s animation has none).
      const held = __BEATME_DEBUG__ && this.play?.holdDuel === true;
      this.duelIntro = showDuelIntro(this.deps.overlay, {
        senderName: this.ctx.opponent.nickname,
        sourceEndM: duel.sourceEndM,
        countdownMs: held ? 60 * 60 * 1000 : duelCountdownMs(!untaught),
        showTeach: untaught,
        onTick: (n) => audio.play(n === 0 ? 'revive' : 'count'),
        // §4bis.31: a hand-back asks the opposite question, so the banner has
        // to say so. `frontierM` falls back to the sender's own end — the
        // block is optional on the wire (types.ts's tolerance rule), and a
        // hand-back's source end IS where the life had got to.
        ...(isHandoff(duel)
          ? {
              handoff: {
                fromName: this.ctx.opponent.nickname,
                frontierM: this.ctx.handoff?.frontierM ?? duel.sourceEndM,
                startM: duel.startM,
                toIsYou: this.ctx.handoffAddressed === true,
              },
            }
          : {}),
      });
      void this.duelIntro.done.then(() => {
        this.duelIntro = null;
        if (this.ended) return;
        this.launchRun();
      });
      return;
    }
    this.launchRun();
  }

  private launchRun(): void {
    this.hudVisible = true;
    this.running = true;
    if (__BEATME_DEBUG__) this.play?.scene('running');
    this.input.reset();
    this.input.setEnabled(true);
    // Own-chain rerun starts cold with no intro — say why you're racing
    // yourself, or the identical replay reads as a bug (phone playtest).
    if (this.rerunBanner !== null) {
      const text = this.rerunBanner;
      this.rerunBanner = null;
      this.floating.push({
        text,
        sx: LOGICAL_W / 2,
        sy: PLAYER_LANE.top + 52,
        life: FLOATING_TEXT_FRAMES * 2,
        maxLife: FLOATING_TEXT_FRAMES * 2,
        color: this.renderer.getPalette().accent,
        size: 22,
      });
    }
    // Design §8.2「激励视频在每局开跑时预取」. Inert until the SDK exists, which
    // is why it is called every run and not just once.
    ads().prepareRewarded();
    this.loop.start();
    trackEvent('game_start', {
      entryType: this.ctx.entryType,
      trackId: this.ctx.track.id,
      opponentRunId: this.ctx.opponent.runId,
      // §4bis.22: the arm as evaluated on-device (debug forcing included) —
      // the per-arm denominator for the peak diagnostic split.
      peakArm: this.peakArm(),
      // §4bis.28: the arm, and whether THIS run is a duel. Both, because an
      // armed device still plays full runs and T4's guardrail is that ratio.
      duelArm: this.duelArm(),
      duel: this.ctx.duel ? 1 : 0,
      handoffArm: this.handoffArm(),
      handoff: isHandoff(this.ctx.duel) ? 1 : 0,
    });
    if (this.ctx.duel) {
      trackEvent('duel_start', {
        sourceRunId: this.ctx.duel.sourceRunId,
        startFrame: this.ctx.duel.startFrame,
        winFrame: this.ctx.duel.winFrame,
        attempt: this.duelAttempt,
        duelArm: this.duelArm(),
      });
      // §4bis.31: somebody picked the life up. `addressed` is T1 — the WHOLE
      // bet: 帮我 has to summon a SPECIFIC person where 打败我 shouts into a
      // chat. If the addressed device is no likelier to open than a stranger,
      // the second-verb thesis is falsified and the flag goes to 0.
      if (isHandoff(this.ctx.duel) && this.duelAttempt === 0) {
        trackEvent('handoff_take', {
          sourceRunId: this.ctx.duel.sourceRunId,
          startFrame: this.ctx.duel.startFrame,
          addressed: this.ctx.handoffAddressed ? 1 : 0,
          handoffArm: this.handoffArm(),
        });
      }
    }
  }

  private applyThemeVars(): void {
    const p = paletteFor(this.ctx.track.theme);
    const s = this.deps.stage.style;
    s.setProperty('--accent', p.accent);
    s.setProperty('--accent-soft', p.accentSoft);
    s.setProperty('--ink', p.ink);
    s.setProperty('--ink-soft', p.inkSoft);
    s.setProperty('--bg', p.bg);
    s.setProperty('--bg-alt', p.bgAlt);
    s.setProperty('--good', p.good);
    s.setProperty('--bad', p.bad);
    this.deps.stage.style.background = p.bg;
  }

  // --- frame --------------------------------------------------------------

  private update(): void {
    if (!this.running) return;

    const frame = this.playerSim.state.frame;

    // Tutorial stop: freeze *before* simulating this frame, so whatever gesture
    // dismisses the overlay is stamped with this exact frame and clears the
    // obstacle on the perfect bot's timing (TECH_SPEC §4bis.15).
    if (this.tutorial) {
      const step = this.tutorial.stepAt(frame);
      if (step !== null) {
        this.enterTutorialStop(step);
        return;
      }
    }

    const drained =
      __BEATME_DEBUG__ && this.play?.isAutoplay ? this.play.drain(frame) : this.input.drain();
    // Injected tutorial actions lead: they are the reason this frame was held.
    const actions = this.injected.length > 0 ? [...this.injected, ...drained] : drained;
    this.injected.length = 0;
    const events: InputEvent[] = [];
    // §4bis.28/.30: a duel's stream is the SUFFIX, and the server refuses one
    // past this slice's budget — `MAX_INPUT_EVENTS − prefixCount`, since 004's
    // flat 120-event reservation retired with the stop at the line. Cap here
    // too, from the same `prefixCount` and the same function, so the client can
    // never record a run its own server would 422.
    const budget = duelInputBudget(this.ctx.duel);
    // §4bis.30: exhausting the budget is RECORDED, never acted on.
    //
    // The first cut of this ended the run on the exhaustion frame, to avoid
    // "a death the player never made". That was unsound, and the review caught
    // it: the client would stop, but the server re-verifies `prefix ++ suffix`
    // with no truncation (§4bis.30 deleted `untilFrame` precisely so there is
    // only one replay path), so the body would run on inputless server-side and
    // store a LATER death and a LONGER distance than the player was shown — and
    // hand that fake frame to the next slice, which is the exact failure the
    // early stop was written to prevent. There is no fix that keeps the early
    // stop: the server cannot learn where to stop without a client-supplied
    // end frame, i.e. `untilFrame` under another name.
    //
    // So the duel does what a 600-event FULL RUN has always done — drop the
    // input and keep playing — and both sides simulate the identical tail.
    // The flag stays as a diagnostic (`health.duels.budgetExhausted`): the
    // arithmetic says it should never fire, and if it ever does we want the
    // number, not a divergence.
    for (const a of actions) {
      // TECH_SPEC §2.7: ≤600 events per run, ≤4 per frame (sim constants, so
      // the client's budget can never drift from the verifier's).
      if (this.inputs.length >= budget) {
        if (this.ctx.duel) this.duelBudgetExhausted = true;
        break;
      }
      if (events.length >= MAX_EVENTS_PER_FRAME) break;
      const ev: InputEvent = { f: frame, a };
      events.push(ev);
      this.inputs.push(ev);
      if (a === Action.Jump) audio.play('jump');
      else if (a === Action.SlideStart) audio.play('slide');
    }

    this.playerPrev = this.playerCur;
    this.playerSim.step(events.length > 0 ? events : EMPTY_EVENTS);
    this.playerCur = snapshot(this.playerSim);

    this.stepGhost();
    this.tickEffects();

    if (!this.playerCur.dead) {
      this.checkNearMiss();
      this.checkOvertake();
      this.checkDuelSeal();
    }

    if (this.playerSim.state.dead) {
      this.onPlayerDeath();
      return;
    }
    // §4bis.30: the duel does NOT stop at the line. 004 ended the run here, so
    // every survivor moved the ratchet by exactly one second against a
    // twelve-second window — 92% overlap on every hop, which reproduced the
    // defect the duel exists to remove. The 活过了 seal is a mid-run beat now
    // (`checkDuelSeal`, iteration 001's proven machinery) and the run carries on
    // to a real death, so the next slice derives from where the player actually
    // fell. Nothing else ends a duel early — see the budget note above for why
    // running out of input events deliberately does NOT.
    if (this.playerSim.state.finished || this.playerSim.state.frame >= MAX_FRAMES) {
      this.endRun(true);
      return;
    }

    // `&pauseAt=`: stop stepping the instant the player sim reaches the frame.
    // The loop keeps rendering (alpha 0), so the canvas holds that exact frame.
    if (__BEATME_DEBUG__ && this.play?.shouldFreeze(this.playerSim.state.frame)) {
      this.input.setEnabled(false);
      this.loop.pause();
    }
  }

  private stepGhost(): void {
    this.ghostPrev = this.ghostCur;
    if (!this.ghostAlive) return;

    const state = this.ghostSim.state;
    if (state.dead || state.finished || state.frame >= MAX_FRAMES) {
      this.ghostAlive = false;
      if (state.dead) this.ghostDeathX = state.x;
      this.ghostCur = snapshot(this.ghostSim);
      return;
    }

    const frame = state.frame;
    const events: InputEvent[] = [];
    while (this.ghostCursor < this.ghostInputs.length) {
      const ev = this.ghostInputs[this.ghostCursor];
      if (!ev || ev.f > frame) break;
      events.push(ev);
      this.ghostCursor++;
    }
    this.ghostSim.step(events);
    const after = this.ghostSim.state;
    if (after.revivesUsed > this.ghostRevives) {
      this.ghostRevives = after.revivesUsed;
      this.ghostReviveMarks.push(after.x);
    }
    if (after.dead) {
      this.ghostAlive = false;
      this.ghostDeathX = after.x;
    } else if (after.finished) {
      this.ghostAlive = false;
    }
    this.ghostCur = snapshot(this.ghostSim);
  }

  private tickEffects(): void {
    if (this.flashFrames > 0) this.flashFrames--;
    if (this.floating.length > 0) {
      for (const f of this.floating) f.life--;
      this.floating = this.floating.filter((f) => f.life > 0);
    }
  }

  /** Design §2.5 beat 1: grazing a death box slows time for 20 frames. */
  private checkNearMiss(): void {
    const s = this.playerSim.state;
    const hitW = s.sliding ? HITBOX_SLIDE_W : HITBOX_STAND_W;
    const hitH = s.sliding ? HITBOX_SLIDE_H : HITBOX_STAND_H;
    const left = s.x;
    const right = s.x + hitW;

    for (let i = 0; i < this.obstacles.length; i++) {
      const o = this.obstacles[i];
      if (!o) continue;
      if (o.x + o.w < left - FQ_ONE) continue;
      if (o.x > right + FQ_ONE) break;
      if (this.nearMissed.has(i)) continue;

      let clearance = -1;
      if (o.type === 'spike' && right > o.x && left < o.x + o.w) {
        clearance = s.y - SPIKE_TOP_FQ;
      } else if (o.type === 'bar' && right > o.x && left < o.x + o.w) {
        clearance = BAR_BOTTOM_FQ - (s.y + hitH);
      } else if (o.type === 'gap') {
        // Landing just past the far edge counts as a near miss.
        if (s.grounded && this.lastGapIndex === i && left >= o.x + o.w) {
          clearance = left - (o.x + o.w);
        } else if (right > o.x && left < o.x + o.w) {
          this.lastGapIndex = i;
        }
      }

      if (clearance >= 0 && clearance <= NEAR_MISS_FQ) {
        this.nearMissed.add(i);
        this.loop.slowMo(NEAR_MISS_SLOWMO_FRAMES, NEAR_MISS_TIMESCALE);
        audio.play('nearMiss');
      }
    }
  }

  /** Design §2.5 beat 2: crossing the opponent's final distance. In the peak
   *  arm (§4bis.22) the beat amplifies into a run-state change: slow-mo bigger
   *  and deeper than the near miss, defeated lane wash, lead counter — all of
   *  which key off `ctx.peak` so the off-arm frame stays pixel-identical. */
  private checkOvertake(): void {
    if (this.overtaken) return;
    const meters = this.playerMeters();
    if (meters <= this.ctx.opponent.scoreM) return;
    this.overtaken = true;
    this.flashFrames = FLASH_FRAMES;
    // §4bis.30: inside a duel the amplification is UNCONDITIONAL. In a duel the
    // opponent's `scoreM` IS `sourceEndM`, so this beat fires exactly on the
    // frame you cross their grave — the duel IS the peak (§4bis.28's own
    // reasoning), and gating it on `PEAK_PCT` would make the duel's headline
    // moment depend on an unrelated experiment's coin flip.
    if (this.ctx.peak || this.ctx.duel) {
      this.loop.slowMo(OVERTAKE_SLOWMO_FRAMES, OVERTAKE_TIMESCALE);
    }
    audio.play('overtake');
    this.floating.push({
      text: t('beat.overtake', { name: this.ctx.opponent.nickname }),
      sx: LOGICAL_W / 2,
      // Above the runner's head — on the figure it fought the flash for legibility.
      sy: PLAYER_LANE.top + 52,
      life: FLOATING_TEXT_FRAMES,
      maxLife: FLOATING_TEXT_FRAMES,
      color: this.renderer.getPalette().accent,
      size: 30,
    });
    if (typeof navigator.vibrate === 'function') navigator.vibrate(30);
  }

  /**
   * §4bis.30 — the 活过了 seal, fired MID-RUN on the frame the duel's line is
   * passed. The run does not stop; it visibly changes state and carries on.
   *
   * Two beats, one second apart, and they cannot collide: `checkOvertake` fires
   * at the sender's grave `E` (their `scoreM` IS `sourceEndM` in a duel), this
   * fires at `L = E + DUEL_SURVIVE_FRAMES = E + 60`, and the seal's slow-mo runs
   * 42 frames — shorter than the gap by construction.
   *
   * **It cannot interrupt play because it is not DOM.** The stamp draws on the
   * canvas, over the OPPONENT's lane (they are dead by construction, so nothing
   * the player must read is behind it). No overlay, no dismiss, no share ask, no
   * `result_view` — the share ask stays where iteration 001 proved it belongs,
   * the death frame.
   */
  private checkDuelSeal(): void {
    const duel = this.ctx.duel;
    if (!duel || this.duelSealed) return;
    if (this.playerSim.state.frame < duel.winFrame) return;
    this.duelSealed = true;

    // The SAME first draw the result screen makes off the same seeded stream,
    // so the word that seals the moment is the word on the result — and a
    // Layer-3 baseline stays stable.
    const roll = mulberry32((this.runSeed ^ 0x51ab5ea1) >>> 0);
    const stamp = pick(duelStampPool('win'), roll, this.duelParams(duel, this.playerMeters()));
    this.duelSealStamp = { text: stamp, start: performance.now() };

    // §4bis.31 — THE PLANT. One extra canvas line under the stamp, on the same
    // non-DOM `floating` layer that provably cannot take a tap.
    //
    // **Why the choice is planted here and MADE at death.** The owner asked for
    // the question at the moment of passing. A question that can STOP the run
    // cannot be built soundly: a voluntary mid-run stop needs the server to
    // learn a client-supplied end frame, which is `untilFrame` under another
    // name — deleted by §4bis.30 with a named tripwire, because it made the
    // server store a death the player never saw and feed that phantom frame to
    // the ratchet. And there is a second, plainer reason: **at the seal the
    // margin is +0 m**, so there is nothing to hand over yet. Every frame you
    // survive from here makes the gift better, which is exactly what this line
    // says — and it converts 005's automatic continuation from a default into
    // a purpose, at the instant the owner wanted.
    //
    // Zero interaction, zero frames, no `result_view`. Drawn only in the arm.
    this.duelSealPlant = this.ctx.handoffArm === true ? t('duel.sealPlant') : null;

    this.loop.slowMo(DUEL_SEAL_SLOWMO_FRAMES, DUEL_SEAL_TIMESCALE);
    audio.play('overtake');
    if (typeof navigator.vibrate === 'function') navigator.vibrate([30, 40, 30]);

    // Coverage for the mid-run beat itself. It must NEVER fire `result_view`:
    // the run is still going, and `result_view` is contractually once per
    // `game_end` (§4bis.22).
    const frame = this.playerSim.state.frame;
    trackEvent('duel_seal', {
      endFrame: frame,
      marginFrames: duelMarginFrames(frame, duel),
      duelArm: this.duelArm(),
    });
  }

  private render(alpha: number): void {
    if (!this.ready) return;
    const gapM = this.hudVisible ? this.playerMeters() - this.ctx.opponent.scoreM : null;
    const duel = this.ctx.duel;
    // Peak arm only: once overtaken, the gap readout dresses as a lead counter
    // and the opponent lane washes toward defeated. False/null outside the arm.
    //
    // §4bis.30: inside a duel the same state change is driven by the SEAL, not
    // by `PEAK_PCT` — the duel is its own peak, and its presentation must not
    // vary with an unrelated experiment's arm. From the seal the opponent lane
    // holds defeated and the gap meter takes its third mode, 「你的地盘 +{d} 米」:
    // ahead is track no one in this chain has reached.
    const overtaken = duel ? this.duelSealed : this.ctx.peak && this.overtaken;
    const accentLine =
      duel && this.duelSealed
        ? t('hud.territory', { d: duelMarginM(this.playerMeters(), duel) })
        : overtaken && gapM !== null && gapM > 0
          ? t('hud.lead', { d: gapM })
          : null;
    // The stamp is wall-clock timed and self-expiring: once faded it stops being
    // drawn, and the state change it announced stays. `&sealhold=1` pins it at
    // full size for a deterministic capture — a beat that plays DURING the run
    // has no frame a screenshot can reliably land on otherwise.
    const stamp = this.duelSealStamp;
    const held = __BEATME_DEBUG__ && this.play?.holdSeal === true;
    const stampElapsed = stamp ? (held ? 200 : performance.now() - stamp.start) : 0;
    const duelSeal =
      stamp && stampElapsed <= DUEL_SEAL_HOLD_MS
        ? {
            text: stamp.text,
            elapsedMs: stampElapsed,
            // §4bis.31: the hand-back plant, drawn under the stamp and fading
            // with it. Absent outside the arm ⇒ the seal is byte-identical to
            // what 005 shipped, which is what the Layer-3 baselines assert.
            ...(this.duelSealPlant ? { plant: this.duelSealPlant } : {}),
          }
        : null;
    const scene: Scene = {
      frame: this.ready ? this.playerSim.state.frame : 0,
      obstacles: this.obstacles,
      playerPrev: this.playerPrev,
      player: this.playerCur,
      ghostPrev: this.ghostPrev,
      ghost: this.ghostCur,
      ghostDead: this.ghostDeathX !== null,
      ghostDeathX: this.ghostDeathX,
      ghostReviveMarks: this.ghostReviveMarks,
      ghostName: this.ctx.opponent.nickname,
      playerName: t('lane.you'),
      ghostReviveLabel: t('marker.revive'),
      distanceM: this.playerMeters(),
      gapM,
      overtaken,
      // Carries either §4bis.22's lead counter or §4bis.30's territory line:
      // one accent readout in one place, two things it can be saying.
      leadText: accentLine,
      duelSeal,
      // §4bis.30: the grave, ticked on the player's own lane.
      //
      // Gated on the OVERTAKE, not on the seal — measured, not assumed. The
      // seal fires a full second past the grave, by which point the mark is
      // ~14.5 grids behind the runner and the renderer has already culled it:
      // gating it on `duelSealed` drew nothing, ever. The overtake fires ON the
      // grave, which is the frame the spec's "anchors at sourceEndM and scrolls
      // away behind you" actually describes.
      duelMark:
        duel && this.overtaken
          ? {
              x: this.startX + duel.sourceEndM * FQ_PER_METER,
              label: t('duel.ghostMark', { name: clampStampName(this.ctx.opponent.nickname) }),
            }
          : null,
      fair: this.ctx.fair,
      fairDeathPose: this.fairDeathPose,
      hudVisible: this.hudVisible,
      metersLabel: t('hud.meters'),
      floating: this.floating,
      flash: this.flashFrames / FLASH_FRAMES,
      slowMo: this.loop.slowMoActive,
    };
    this.renderer.draw(scene, alpha);

    // §4bis.23: the two-box flash over the frozen death photo. Under a revive
    // offer the loop is paused-but-rendering, so this animates on the wall
    // clock and stops being drawn the instant a revive clears `deathFlash`.
    // Once the run ends the loop is stopped and `endRun` has already settled
    // the flash (§4bis.27), so the final frame carries the end state.
    if (this.deathFlash) {
      const f = this.deathFlash;
      this.renderer.drawDeathFlash({
        playerBox: f.playerBox,
        obstacleBox: f.obstacleBox,
        // playerPrev was snapped to the death state when the flash armed, so
        // the scene camera is exactly the death-frame camera at any alpha.
        camX: this.playerCur.x,
        elapsedMs: f.settled ? 600 : performance.now() - f.start,
        chip: t('hud.deathBox'),
      });
    }
  }

  private playerMeters(): number {
    return Math.max(0, metersFromFQ(this.playerSim.state.x - this.startX));
  }

  // --- tutorial stops -----------------------------------------------------

  /** Hold the frame and ask for the gesture. Deliberately NOT the §4.5
   *  interruption path: no countdown, and no PauseEvent — the server never
   *  learns that this run was taught, so verification is unaffected. */
  private enterTutorialStop(step: TutorialStep): void {
    if (this.tutorialUi) return;
    // Disabled input is what keeps the dismissing gesture from being recorded
    // twice: the overlay decides, `this.injected` records.
    this.input.setEnabled(false);
    this.loop.pause();
    if (__BEATME_DEBUG__) this.play?.scene('tutorial');
    this.tutorialUi = showTutorialStep(this.deps.overlay, {
      step,
      onAct: () => this.leaveTutorialStop(step),
    });
  }

  private leaveTutorialStop(step: TutorialStep): void {
    this.tutorialUi?.remove();
    this.tutorialUi = null;
    this.tutorial?.consume();
    // The player's own action, on the frame they were shown. `update` picks it
    // up on the next tick, still at the frozen frame, and records it like any
    // thumb press — SFX, event stream, server verification all included.
    this.injected.push(actionForStep(step));
    audio.unlock();
    if (__BEATME_DEBUG__) this.play?.scene('running');
    this.input.reset();
    this.input.setEnabled(true);
    this.loop.resume();
  }

  // --- interruptions ------------------------------------------------------

  private readonly onResize = (): void => {
    this.renderer.resize();
    this.render(0);
  };

  /** Design §4.5: system-triggered pause only, resumed with a 3-2-1 countdown. */
  private readonly onVisibility = (): void => {
    if (!this.running || this.ended) return;
    // A tutorial stop already holds the frame with its own overlay; layering the
    // 3-2-1 countdown on top would resume the loop straight back into the stop.
    if (this.tutorialUi) return;
    if (document.visibilityState === 'hidden') {
      if (this.pendingPause) return;
      this.pendingPause = true;
      this.pauseStartedAt = Date.now();
      this.pauseFrame = this.playerSim.state.frame;
      this.input.setEnabled(false);
      this.loop.pause();
      return;
    }
    if (!this.pendingPause) return;
    this.pendingPause = false;
    this.pauses.push({ f: this.pauseFrame, d: Math.max(0, Date.now() - this.pauseStartedAt) });
    const handle = showPause(this.deps.overlay);
    void handle.resume((n) => audio.play(n === 0 ? 'revive' : 'count')).then(() => {
      if (this.ended) return;
      this.input.reset();
      this.input.setEnabled(true);
      this.loop.resume();
    });
  };

  // --- death, revive, end -------------------------------------------------

  private onPlayerDeath(): void {
    this.running = false;
    this.input.setEnabled(false);
    this.loop.pause();
    audio.play('death');

    // §4bis.23/.27/.30 — the death flash has ABSOLUTE precedence over the seal.
    // A duel winner can pass the line and die a second later, inside the seal's
    // own 1.4 s window; the two must never share the screen, and the flash must
    // not animate against the seal's stretched clock. So the stamp is dropped
    // and real time restored HERE, before the flash is armed below. `duelSealed`
    // deliberately survives: the verdict is still 活过了, and the lane wash and
    // grave tick are the frame's honest state.
    this.duelSealStamp = null;
    this.loop.clearSlowMo();

    // §4bis.23: remember the in-flight pose (the dead camera comp settles on
    // it instantly), and on a fair-arm COLLISION death arm the two-box flash.
    // Gap falls stay unexplained on purpose — they explain themselves.
    const dead = this.playerSim.state;
    this.fairDeathPose = dead.sliding ? 'slide' : 'stand';
    if (this.ctx.fair) {
      const hit = collidingObstacle(
        { x: dead.x, y: dead.y, sliding: dead.sliding },
        this.obstacles,
      );
      const obstacleBox = hit ? obstacleKillBox(hit) : null;
      if (obstacleBox) {
        // Snap the interpolation to the death frame: the photo must show the
        // body the boxes explain, not the frame before it.
        this.playerPrev = { ...this.playerCur };
        this.deathFlash = {
          playerBox: playerKillBox(dead.x, dead.y, dead.sliding),
          obstacleBox,
          start: performance.now(),
          settled: __BEATME_DEBUG__ ? this.play?.isAutoplay === true : false,
        };
      }
    }

    // Autoplay holds the death frame (the shot the harness wants) unless the URL
    // explicitly asks for the result screen. The revive offer never fires: it is
    // a 3-second wall-clock countdown, which no deterministic capture can hit.
    if (__BEATME_DEBUG__ && this.play?.isAutoplay) {
      this.play.scene('dead');
      if (!this.play.showResult) {
        this.play.freeze();
        return;
      }
      this.endRun(false);
      return;
    }

    // §4bis.28: NO revives in a duel — not the newbie offer, not the ad offer.
    // A revive trivially defeats a 60-frame line, and `revives = 0` is what
    // keeps duel rows inert in the §3.2 comparator. The retry is instant and
    // free instead, which is the better answer at this length anyway.
    if (this.ctx.duel) {
      this.endRun(false);
      return;
    }

    const manager = ads();
    const kind = reviveOffer({
      firstEver: this.ctx.firstEver,
      newbieReviveUsed: this.newbieReviveUsed,
      adReviveUsed: this.adReviveUsed,
      revives: this.revives,
      canRecordRevive: this.reviveFits(),
      adsEnabled: manager.enabled,
      rewardedReady: manager.rewardedReady(),
    });
    if (kind === null) {
      this.endRun(false);
      return;
    }
    // Marked on the offer, not on the acceptance: declining ends the run, so a
    // second offer of the same kind can never be honest anyway.
    if (kind === 'newbie') this.newbieReviveUsed = true;
    else this.adReviveUsed = true;

    void showReviveOffer(this.deps.overlay, { seconds: REVIVE_OFFER_SECONDS, kind }).then(
      (taken) => {
        // Same guard `showAdThenRevive` carries: this resolves a wall-clock
        // countdown later, and reviving into an already-ended run would push a
        // Revive event and restart the loop under a mounted result screen or
        // peak seal. Unreachable today — nothing else ends the run during the
        // offer — which is exactly why it is cheap to keep true.
        if (this.ended) return;
        if (!taken) {
          this.endRun(false);
          return;
        }
        if (kind === 'newbie') {
          this.doRevive();
          return;
        }
        void this.showAdThenRevive();
      },
    );
  }

  /**
   * Can a `Revive` event still be recorded on the death frame? TECH_SPEC §2.7
   * caps a run at 600 events and 4 per frame, and `simulateRun` rejects a
   * stream that breaks either — so a death frame already carrying 4 actions
   * must not be offered a revive at all, or the whole run would come back 422
   * after the player watched an ad for it.
   */
  private reviveFits(): boolean {
    if (this.inputs.length >= MAX_INPUT_EVENTS) return false;
    const deathFrame = this.playerSim.state.deathFrame;
    let onFrame = 0;
    for (let i = this.inputs.length - 1; i >= 0; i--) {
      const ev = this.inputs[i];
      if (!ev || ev.f < deathFrame) break; // ascending stream: nothing earlier matches
      if (ev.f === deathFrame) onFrame++;
    }
    return onFrame < MAX_EVENTS_PER_FRAME;
  }

  /** Design §8.1: the revive is earned only by a *viewed* ad; a dismissed or
   *  broken one lands on the result screen exactly like declining would. */
  private async showAdThenRevive(): Promise<void> {
    const outcome = await ads().showRewarded();
    if (this.ended) return;
    if (outcome === 'viewed') this.doRevive();
    else this.endRun(false);
  }

  private doRevive(): void {
    const deathFrame = this.playerSim.state.deathFrame;
    // TECH_SPEC §2.7: Revive is only valid at the frame the sim reported death.
    const ev: InputEvent = { f: deathFrame, a: Action.Revive };
    this.inputs.push(ev);
    this.playerSim.step([ev]);
    if (this.playerSim.state.dead) {
      // Sim refused the revive — end the run rather than desync with the server.
      this.endRun(false);
      return;
    }
    this.revives++;
    // §4bis.23: the flash and the dead comp clear the instant a revive
    // resumes — the explanation belongs to a death that stuck.
    this.deathFlash = null;
    this.fairDeathPose = null;
    this.playerCur = snapshot(this.playerSim);
    this.playerPrev = { ...this.playerCur };
    audio.play('revive');
    this.running = true;
    this.input.reset();
    this.input.setEnabled(true);
    this.loop.resume();
  }

  private endRun(finished: boolean): void {
    if (this.ended) return;
    this.ended = true;
    this.running = false;
    this.hudVisible = false;
    // §4bis.23 + §4bis.27: settle the flash BEFORE stopping the loop. The flash
    // is wall-clock driven and only animates while the loop keeps rendering —
    // which it does under a revive offer (`pause`), but NOT here. `stop()` kills
    // the rAF, so the trailing `render()` at the end of the current tick is the
    // last frame the canvas ever gets; without this the boxes would be frozen at
    // `elapsedMs ≈ 0`, i.e. outline alpha 0, no overlap fill and no `hud.deathBox`
    // chip — the flash would draw nothing at all on every death that isn't
    // behind a revive offer, which is most of them.
    if (this.deathFlash) this.deathFlash.settled = true;
    this.loop.stop();
    this.input.setEnabled(false);
    this.tutorialUi?.remove();
    this.tutorialUi = null;
    this.duelIntro?.dismiss();
    this.duelIntro = null;
    // Taught, however far the run got. A player who died on obstacle #1 has met
    // the tutorial; replaying the whole thing next run reads as the game not
    // remembering them. 「下一局重新教学」 on this very screen is the way back.
    // A duel never had a Tutorial (it is forced off), so this is a no-op there
    // and the device's next FULL run still gets its stop frames.
    this.tutorial?.finish();

    const state = this.playerSim.state;
    const distanceM = this.playerMeters();
    const deathFrame = state.dead ? state.deathFrame : -1;

    // §4bis.28/.30: the duel verdict, judged by survival and never by the score
    // comparator. `endFrame` is where the body really stopped — the death
    // frame, or the frame the run reached alive (MAX_FRAMES, the finish, or a
    // budget exhaustion). Passing the line is the win; dying afterwards does not
    // take it back.
    const duel = this.ctx.duel;
    const duelEndFrame = state.dead ? state.deathFrame : this.playerSim.state.frame;
    if (duel) this.duelOutcome = duelOutcomeFor(duelEndFrame, duel);

    const endProps: GameEndProps = {
      distanceM,
      deathFrame,
      finished,
      revives: this.revives,
      entryType: this.ctx.entryType,
      peakArm: this.peakArm(),
      duelArm: this.duelArm(),
      duel: duel ? 1 : 0,
      handoffArm: this.handoffArm(),
      handoff: isHandoff(duel) ? 1 : 0,
    };
    trackEvent('game_end', endProps);
    if (duel && this.duelOutcome) {
      trackEvent('duel_result', {
        outcome: this.duelOutcome,
        endFrame: duelEndFrame,
        // §4bis.30: how far past the grave they really got — in frames and in
        // metres. Under 004 a win was always exactly +60 f, so there was
        // nothing to report; this is the read T5 needs and the number the share
        // card finally says out loud.
        marginFrames: duelMarginFrames(duelEndFrame, duel),
        marginM: duelMarginM(distanceM, duel),
        budgetExhausted: this.duelBudgetExhausted ? 1 : 0,
        retries: this.duelAttempt,
        duelArm: this.duelArm(),
      });
    }
    // Design §8.2「广告 SDK 不计入 200KB 核心包，首局 game_end 之后才懒加载」:
    // the very first run is never slowed down by an ad network. No-op after the
    // first call, and while the server flag is off.
    ads().noteGameEnd();
    markPlayed();

    // §4bis.28: a duel submits the SUFFIX ALONE plus the source id. The server
    // rebuilds the prefix from that run's own stored blob, so there is nothing
    // here worth forging — a hostile source id only chooses which honest prefix
    // you must survive. `duelStartFrame` is an echo for cross-check; it can be
    // refused, never obeyed.
    //
    // `parentRunId` rides only the FIRST attempt (`duelParentRunId`): retries
    // and 回敬 volleys attach nothing, exactly as ladder rungs do (§4bis.21).
    const duelParent = duel ? duelParentRunId(duel, this.duelAttempt, this.ctx.entryType) : undefined;
    const parentRunId = duel ? duelParent : this.ctx.parentRunId;
    const body: SubmitRunBody = {
      trackId: this.ctx.track.id,
      inputs: this.inputs,
      pauses: this.pauses,
      entryType: this.ctx.entryType,
      locale: getLocale(),
      ...(this.ctx.opponent.runId ? { opponentRunId: this.ctx.opponent.runId } : {}),
      ...(parentRunId ? { parentRunId } : {}),
      ...(duel
        ? {
            duelSourceRunId: duel.sourceRunId,
            duelStartFrame: duel.startFrame,
            // §4bis.31: the verb the SERVER told us this link was, echoed back
            // so it re-derives the same way. Not a claim that buys anything —
            // both derivations are pure functions of the stored blob, so this
            // only chooses WHICH honest prefix we had to survive, and a
            // disagreement 422s `duel_start_mismatch` on the one integer they
            // differ by.
            ...(isHandoff(duel) ? { duelMode: 'handoff' as const } : {}),
            // §4bis.29: proof the server itself served us this run. Sent only
            // on a duel, because attachment is the only thing it decides.
            ...(this.ctx.deliveryToken ? { deliveryToken: this.ctx.deliveryToken } : {}),
          }
        : {}),
    };

    // §4bis.22: classify EVERY run (both arms — the coverage read starts while
    // the flag is still 0); only the seal itself is behind the gate. The revive
    // offers kept precedence by construction: endRun only runs once they are
    // declined, exhausted or refused by the sim.
    const outcome = this.localOutcome(distanceM);
    const oppEndFrame =
      this.ctx.opponent.deathFrame >= 0 ? this.ctx.opponent.deathFrame : MAX_FRAMES;
    const playerEndFrame = state.dead ? state.deathFrame : MAX_FRAMES;
    const kind = peakFor(outcome, state.dead, playerEndFrame, oppEndFrame);
    this.peakCoverage = peakCoverageFor(outcome, state.dead, playerEndFrame, oppEndFrame, finished);

    const autoplaying = __BEATME_DEBUG__ && this.play?.isAutoplay === true;

    // §4bis.28: the duel seal. It reuses ui/peak.ts but mounts INDEPENDENTLY of
    // `PEAK_PCT` — the duel *is* the peak, and the whole point of stopping at
    // the line is that the moment you pass his grave is the shareable one. An
    // instant death seals nothing: a trophy for a start-line accident would be
    // a lie, and the free retry is the kinder answer.
    if (duel && this.duelOutcome && duelSeals(this.duelOutcome)) {
      const showable =
        !autoplaying || (__BEATME_DEBUG__ && this.play?.showPeak === true);
      if (showable && navigator.onLine !== false) {
        this.showDuelSeal(this.duelOutcome, distanceM, finished, body, autoplaying);
        return;
      }
    }

    if (
      shouldShowSeal({
        peakActive: this.ctx.peak,
        kind,
        autoplay: autoplaying,
        // Autoplay mounts the seal only when the URL asks (`&peakui=1`) — the
        // tutorial precedent: a surprise overlay would hang every baseline.
        showPeak: __BEATME_DEBUG__ && this.play?.showPeak === true,
        // Offline at death time: skip mounting entirely (no mount-then-vanish
        // flicker); the result screen owns the offline note.
        online: navigator.onLine !== false,
      })
    ) {
      this.showSeal(kind as PeakKind, distanceM, finished, body, autoplaying);
      return;
    }

    this.showResult(distanceM, finished);

    // A bot run is not a run: it never reaches the leaderboard, the chain or the
    // stranger pool. The screen it produces is local data only.
    if (autoplaying) {
      this.applyDemoResult(distanceM);
      return;
    }

    void this.submit(body, distanceM);
  }

  /**
   * §4bis.31 — promote 帮我 once the run is known to be hand-backable.
   *
   * The offer is a server fact that arrives with the submit response, so the
   * verb is promoted here rather than guessed at mount. T1's denominator is
   * recorded here too, because this is where the ask is actually MADE:
   * recording it at mount would count offers that were never shown, which is
   * the silent-degradation disease pointed at the metric instead of the player.
   *
   * **`&handoffmint=1` is honoured HERE and nowhere else**, and that placement
   * is the whole point. It first forced the answer at MOUNT, which meant the
   * button was built by the ordinary path and this method never ran — so the
   * flag excluded the only path a real client takes, and `handoff-result` came
   * back green on a build whose promoted button could not be pressed. A debug
   * affordance must enter the production path, not parallel it. Rollup drops
   * this branch from a normal build.
   */
  private promoteHandoff(distanceM: number): void {
    const view = this.result;
    if (!view) return;
    const forced = __BEATME_DEBUG__ && this.play?.forceHandoffMint === true;
    const offered = forced || this.handoffOffered(distanceM);
    this.noteHandoffOffer(distanceM, offered);
    if (!offered || this.handoffShown) return;
    this.handoffShown = true;
    // §4bis.31 — **the stamp moves with the verb, or they argue with each other.**
    //
    // The gift voice used to be decided at MOUNT, from `handoffOffered`. But the
    // answer does not exist at mount on a full run (`endRun` shows the result
    // screen and submits afterwards), so `gift` was always false there and
    // `pool.handoffStamp` could never render on the ~40% majority path — the
    // exact path the mint gate exists for. The screen ended up reading
    // 「线是你的了」 (the plunder voice) above a button reading 「交回给 {name}」,
    // which is the disagreement `deathStampPool` was written to prevent.
    //
    // Redrawn here because this is the one place that knows the answer AND is
    // reached by both callers. `pool.handoffStamp` interpolates nothing (a stamp
    // is not a receipt), and the title is the FIRST draw off
    // `mulberry32(runSeed)` on both mount paths — so re-drawing it from a fresh
    // roll of the same seed reproduces exactly what mount would have drawn had
    // the answer been known, and the Layer-3 baseline stays deterministic.
    view.setTitle(pick('pool.handoffStamp', mulberry32(this.runSeed), {}));
    view.promotePrimary({
      label: this.handoffCtaLabel(distanceM),
      needsShareCode: true,
      onClick: () => void this.doShare(distanceM, this.ctx.duel ? 'duel' : 'result', 'handoff'),
    });
  }

  /** The autoplay result screen's local-only state (no submit ever happens). */
  private applyDemoResult(distanceM: number): void {
    this.result?.setNote(null);
    this.result?.setShareReady();
    this.result?.setNickname(DEMO_NICKNAME);
    // §4bis.31: autoplay never submits, so the promotion it would otherwise
    // never reach is driven here — through the SAME method production uses.
    this.promoteHandoff(distanceM);
    // Nothing was submitted, so there is no run to exclude from 本周的你 —
    // and no wait for one either. This is the path the ladder shot captures.
    if (this.ctx.ladder) void this.loadLadder(distanceM);
  }

  /** `peakArm` event prop: the arm as evaluated on-device (§4bis.22). */
  private peakArm(): 0 | 1 {
    return this.ctx.peak ? 1 : 0;
  }

  /** `duelArm` event prop: the arm as evaluated on-device (§4bis.28). Reported
   *  on FULL runs too — T4's guardrail is duels vs full runs per armed device. */
  private duelArm(): 0 | 1 {
    return this.ctx.duelArm ? 1 : 0;
  }

  /** §4bis.31: the handoff arm as evaluated on-device. Reported on every event
   *  in BOTH arms — T2 is a share of the arm's eligible deaths, so the
   *  denominator has to exist before anyone chooses a verb. */
  private handoffArm(): 0 | 1 {
    return this.ctx.handoffArm ? 1 : 0;
  }

  // --- the duel (快斗, §4bis.28) --------------------------------------------

  /**
   * Params shared by the duel stamp, line and CTA pools.
   *
   * `n` is deliberately signed by outcome: on a LOSS it is how far short of the
   * grave you fell (`duelGapM`), on a WIN it is how far past it you got
   * (`duelMarginM`, §4bis.30). Both pools ask "how far?" about the same line,
   * and exactly one of the two is ever non-zero — so one slot, read from the
   * side of the line the player ended on. 004 could only ever put "+1 秒" here.
   */
  /**
   * §4bis.31 — **the verb precedence**, and the one place it is decided.
   *
   * `handoffEligible` ⇒ 帮我 primary, 打败我 secondary; otherwise 打败我 only.
   * Both verbs on every qualifying death is what the pivot requires; this rule
   * is its general form, and keeping it in ONE predicate is what lets the
   * severance in §12 (collapse to the precedence-chosen verb, other behind a
   * text link) be a one-line change if `share_click/game_end` falls.
   *
   * Gated on the arm, so at `HANDOFF_PCT = 0` this is always false and both
   * result screens are byte-for-byte what 005 shipped.
   */
  private handoffOffered(distanceM: number): boolean {
    if (this.ctx.handoffArm !== true) return false;
    // §4bis.31 — **the offer is the SERVER's answer, not our guess.**
    //
    // This used to read `handoffEligible(deathFrame, slice)`, i.e. "did the run
    // get past frame 1200". That is the eligibility rule and not the mint rule:
    // eligible is ~40% of deaths, and only ~52% of those actually mint a
    // hand-back slice. The rest degraded SILENTLY into an ordinary challenge —
    // the sender believed they had handed their run to a friend, the friend
    // received a normal race, and neither could tell. Roughly half of all
    // hand-back attempts did that.
    //
    // So the button waits for `handoffMintable`, which the server derives with
    // the same function the recipient's boot will run. Before the submit lands
    // this is false and the screen shows 打败我 only — the truthful state, not a
    // degraded one — and `applySubmitted` promotes the verb once the answer
    // arrives. Share buttons are disabled until then anyway, so nothing
    // tappable ever makes a promise the link cannot keep.
    if (this.submitted?.handoffMintable !== true) return false;
    // The gift has to be positive to be a gift. On a duel this is the margin
    // past the grave; on a full run every metre is yours by construction.
    return this.handoffAddedM(distanceM) > 0;
  }

  /** What YOU added — the personal number, and the only one that is yours.
   *  `score_m` on a hand-back is nobody's: the stream has several authors. */
  private handoffAddedM(distanceM: number): number {
    const duel = this.ctx.duel;
    return duel ? duelMarginM(distanceM, duel) : distanceM;
  }

  /**
   * The 帮我 button's label, and the ONE place the two hand-back situations are
   * told apart.
   *
   * A duel death has a person before you — the owner of the grave you just
   * passed — so the run is handed BACK and the button names them. A FULL RUN's
   * death does not: you started this life, so it is handed ON, and there is
   * nobody to name. Reusing the duel label here rendered 「交回给 刚才的你」
   * ("Hand it to Past you"), because `lane.pastYou` is the ghost's label from a
   * different feature entirely.
   */
  private handoffCtaLabel(distanceM: number): string {
    const n = this.handoffAddedM(distanceM);
    return this.ctx.duel
      ? t('handoff.cta', { name: clampStampName(this.ctx.opponent.nickname), n })
      : t('handoff.ctaOwn', { n });
  }

  private duelParams(duel: DuelSlice, distanceM: number): Record<string, string | number> {
    const gap = duelGapM(distanceM, duel);
    return {
      name: clampStampName(this.ctx.opponent.nickname),
      d: duel.sourceEndM,
      p: distanceM,
      n: gap > 0 ? gap : duelMarginM(distanceM, duel),
      m: distanceM,
    };
  }

  /**
   * The duel seal. Same overlay as §4bis.22's, deliberately: the seal is a
   * solved surface and a second one would be a second thing to maintain. What
   * differs is that this one mounts on its own authority (the duel IS the peak,
   * so it is not behind `PEAK_PCT`), it carries a receipt line under the stamp,
   * and its `share_click` reports `moment: 'duel'`.
   */
  private showDuelSeal(
    outcome: Exclude<DuelOutcome, 'instant'>,
    distanceM: number,
    finished: boolean,
    body: SubmitRunBody,
    autoplaying: boolean,
  ): void {
    if (!autoplaying) void this.submit(body, distanceM);
    const duel = this.ctx.duel!;
    const params = this.duelParams(duel, distanceM);

    // One seeded stream, three draws, in this order — so stamp, line and CTA
    // vary together per run and a baseline stays stable.
    const roll = mulberry32((this.runSeed ^ 0x51ab5ea1) >>> 0);
    // §4bis.31: the seal leads with 帮我 when the run is hand-backable, so its
    // stamp must not be the plunder voice — 「你从 {name} 手里多抢了 {d} 米」
    // over a button that says 「交回给 {name}」 argues with itself. Same stream,
    // same draw order, pool swapped.
    const sealGift = this.handoffOffered(distanceM);
    const stamp = pick(deathStampPool(sealGift, duelStampPool(outcome)) as PoolKey, roll, params);
    const line = pick(duelLinePool(outcome), roll, params);
    const ctaLabel = pick('pool.duelCta', roll, params);

    // §4bis.31 — VERB PRECEDENCE APPLIES HERE TOO, and this is the surface that
    // most needed it.
    //
    // The seal is the FIRST screen a sealed duel shows, and `PeakOverlay` has
    // exactly one CTA. Leaving that CTA as 回敬 put 帮我 one dismissal behind:
    // a player who takes the ask in front of them would never see the second
    // verb at all. That inverts the precedence the spec fixes (eligible ⇒ 帮我
    // primary, 打败我 secondary) and, worse, it hides the verb on precisely the
    // run the owner's directive is about — the one that just passed the grave.
    //
    // The other verb is not lost: dismissing the seal lands on the duel result
    // screen, which offers both. What changes is which one leads.
    // The CTA mounts with the challenge label and is INVISIBLE until
    // `armShare`, which is the moment the submit answers whether this run can
    // be handed back at all — so `armShare` sets the final label and the player
    // sees exactly one verb, once. Deciding it here would mean deciding it
    // before the server has answered, which is the guess this change removes.
    this.peakUi = new PeakOverlay(this.deps.overlay, {
      stamp,
      small: stampShrinks(stamp, getLocale()),
      win: outcome === 'win',
      line,
      ctaLabel,
      dismissHint: t('peak.dismiss'),
      // 回敬 — the ratchet. Sharing YOUR duel run is what moves the line: the
      // next challenger's slice re-derives from it, so a survivor pushes the
      // line a second further and a loser hands back the exact line they failed.
      // 帮我 takes the same slot when the run is hand-backable, because the
      // frontier is worth more passed on than answered.
      onShare: () => {
        // Resolved at CLICK time, not at mount: the CTA is revealed only once
        // the submit has answered, so by the time this can fire the verb is
        // known and matches the label `armShare` applied.
        void this.doShare(distanceM, 'duel', sealVerb(this.handoffOffered(distanceM)));
        this.dismissSeal(distanceM, finished);
      },
      onDismiss: () => this.dismissSeal(distanceM, finished),
    });

    trackEvent('result_view', {
      entryType: this.ctx.entryType,
      distanceM,
      outcome: this.localOutcome(distanceM),
      peak: this.peakCoverage,
      peakArm: this.peakArm(),
      duelArm: this.duelArm(),
      duel: 1,
      duelOutcome: outcome,
      // Without these the seal path reported a `result_view` that could not be
      // split by arm — and the seal is where MOST sealed duels are seen.
      handoffArm: this.handoffArm(),
      handoff: isHandoff(duel) ? 1 : 0,
    });
    this.resultViewSent = true;

    if (autoplaying) this.peakUi.armShare();
  }

  /**
   * Instant death (< `DUEL_MERCY_FRAMES` past the handover): re-run the SAME
   * duel immediately. `duelAttempt` increments, which drops `parentRunId` on
   * every submit after the first — a start-line accident must not be able to
   * add a link to the chain, let alone one per retry.
   */
  private retryDuel(): void {
    this.duelAttempt++;
    this.result?.remove();
    this.result = null;
    this.shareCode = null;
    this.reset();
    this.render(0);
    this.beginRun();
  }

  // --- the seal (决胜一刻, §4bis.22) ---------------------------------------

  /**
   * Hold the death frame under the stamp; the result screen is built when the
   * overlay dismisses. The submit is kicked off FIRST so the shareCode race
   * starts at t=0; the CTA stays hidden until it resolves (ui/peak.ts).
   */
  private showSeal(
    kind: PeakKind,
    distanceM: number,
    finished: boolean,
    body: SubmitRunBody,
    autoplaying: boolean,
  ): void {
    if (!autoplaying) void this.submit(body, distanceM);

    const stamp = this.stampFor(kind, distanceM);
    const ctaRoll = mulberry32((this.runSeed ^ 0x51ab5ea1) >>> 0);
    ctaRoll(); // the stamp consumed the first draw of this stream
    const pool = peakCtaPool(kind, this.ctx.entryType, this.ctx.opponent.official === true);
    const ctaLabel = pick(pool, ctaRoll, this.peakParams(distanceM));

    this.peakUi = new PeakOverlay(this.deps.overlay, {
      stamp: stamp.text,
      small: stamp.small,
      win: kind === 'win',
      ctaLabel,
      dismissHint: t('peak.dismiss'),
      // The existing share path verbatim (same URL incl. ?v=, same text pools,
      // same card) — only the `moment` prop differs. Then straight to the
      // result screen: the sheet floats over it.
      onShare: () => {
        void this.doShare(distanceM, 'peak');
        this.dismissSeal(distanceM, finished);
      },
      onDismiss: () => this.dismissSeal(distanceM, finished),
    });

    // Still the first result surface, still exactly once per game_end — just
    // ~1 s earlier than on a non-peak run (§4bis.22 documents the shift).
    trackEvent('result_view', {
      entryType: this.ctx.entryType,
      distanceM,
      outcome: this.localOutcome(distanceM),
      peak: this.peakCoverage,
      peakArm: this.peakArm(),
    });
    this.resultViewSent = true;

    // Demo mode: nothing is submitted, so the honest-CTA rule is satisfied by
    // decree — the shot needs the revealed row (mirrors setShareReady()).
    if (autoplaying) this.peakUi.armShare();
  }

  /** Seal → result screen, exactly as today (result_view already fired). */
  private dismissSeal(distanceM: number, finished: boolean): void {
    if (!this.peakUi) return;
    this.peakUi.remove();
    this.peakUi = null;
    this.showResult(distanceM, finished);
    if (__BEATME_DEBUG__ && this.play?.isAutoplay) {
      this.applyDemoResult(distanceM);
      return;
    }
    if (this.submitted) this.applySubmitted(this.submitted, distanceM);
    else if (this.submitNote) this.result?.setNote(this.submitNote);
  }

  /** Params shared by the stamp and CTA pools (§4bis.22 copy contract). */
  private peakParams(distanceM: number): Record<string, string | number> {
    const oppScoreM = this.ctx.opponent.scoreM;
    const oppEndFrame =
      this.ctx.opponent.deathFrame >= 0 ? this.ctx.opponent.deathFrame : MAX_FRAMES;
    const state = this.playerSim.state;
    const deathFrame = state.dead ? state.deathFrame : MAX_FRAMES;
    const seconds = Math.max(0.1, (oppEndFrame - deathFrame) / 60);
    return {
      name: clampStampName(this.ctx.opponent.nickname),
      m: distanceM,
      d: Math.abs(distanceM - oppScoreM),
      s: seconds.toFixed(1),
    };
  }

  /** One seeded pick from the stamp pools — a distinct stream from the title's
   *  (`runSeed ^ 0x51ab5ea1`), so seal copy and result copy vary independently. */
  private stampFor(kind: PeakKind, distanceM: number): { text: string; small: boolean } {
    const roll = mulberry32((this.runSeed ^ 0x51ab5ea1) >>> 0);
    const text = pick(
      kind === 'win' ? 'pool.peakStampWin' : 'pool.peakStampNear',
      roll,
      this.peakParams(distanceM),
    );
    return { text, small: stampShrinks(text, getLocale()) };
  }

  private async submit(body: SubmitRunBody, distanceM: number): Promise<void> {
    let res: SubmitRunResponse;
    // ONLY the request may sit inside this try. A throw from onSubmitted —
    // a copy pool, the ladder fetch, any DOM work — would otherwise be read as
    // a failed submit and cache a run the server has already stored, which the
    // next boot would resubmit as a second run.
    try {
      res = await submitRun(body);
    } catch (err) {
      // Only a run the server actually judged may be called unverified, and
      // only a judged run may be dropped (src/submitOutcome.ts).
      const { note, retry } = classifySubmitFailure(err instanceof ApiError ? err.status : -1);
      if (retry) cachePendingRun(body);
      // Held for the seal path: the result screen may not exist yet, and it is
      // the screen — not the overlay — that owns the offline/rejected note.
      this.submitNote = t(note);
      this.result?.setNote(this.submitNote);
      this.peakUi?.submitFailed();
      return;
    }
    this.onSubmitted(res, distanceM);
  }

  private onSubmitted(res: SubmitRunResponse, localDistanceM: number): void {
    this.shareCode = res.shareCode;
    this.submitted = res;
    // Seal up (§4bis.22): the shareCode makes the CTA an honest ask — reveal
    // it. The result view is refined at dismissal from the stored response.
    //
    // §4bis.31: `this.submitted` is set one line above, so `handoffOffered` can
    // now answer, and the seal's CTA gets its verb at the instant it first
    // becomes visible. The seal's own click handler reads the verb lazily, so
    // label and action cannot disagree.
    this.peakUi?.armShare(
      this.handoffOffered(localDistanceM) ? this.handoffCtaLabel(localDistanceM) : undefined,
    );
    if (this.result) this.applySubmitted(res, localDistanceM);
  }

  /** Refine a mounted result screen with the verified response. */
  private applySubmitted(res: SubmitRunResponse, localDistanceM: number): void {
    const view = this.result;
    if (!view) return;
    view.setNote(null);
    view.setShareReady();
    if (res.chain !== undefined) view.setChain(res.chain ?? null);
    if (res.nickname) view.setNickname(res.nickname);
    const outcome = res.outcome ?? this.localOutcome(localDistanceM);
    const oppScoreM = res.oppScoreM ?? this.ctx.opponent.scoreM;
    const scoreM = res.scoreM || localDistanceM;
    view.setTitle(this.titleFor(outcome, scoreM, oppScoreM));
    // Only a run the server verified — and would offer back on rung 2 — may
    // become the remembered best (B4): a rejected, undelivered, flagged or
    // interrupted run is not a record, however far the body ran.
    // IR-H02: the displayed score may fall back to the local distance so the
    // title still reads sensibly, but the CACHE takes only a number the server
    // actually returned. Without this a malformed 200 (no `scoreM`) would write
    // an unverified local number — the precise dishonesty B4 exists to remove.
    if (typeof res.scoreM === 'number') {
      recordVerifiedBest(this.ctx.track.id, {
        scoreM: res.scoreM,
        ...(res.flags ? { flags: res.flags } : {}),
        ...(res.interrupted !== undefined ? { interrupted: res.interrupted } : {}),
      });
    }
    this.promoteHandoff(localDistanceM);
    // Rungs 2 and 3 wait for the verified run: 本周的你 is the device's best
    // INCLUDING this one (B3), and knowing whether it IS this one needs its id.
    if (this.ctx.ladder) void this.loadLadder(scoreM, res.runId);
  }

  private localOutcome(distanceM: number): Outcome {
    const opp = this.ctx.opponent.scoreM;
    if (distanceM > opp) return 'win';
    if (distanceM < opp) return 'lose';
    return 'tie';
  }

  /** Copy pools, design §4.6 — one seeded pick per run. */
  private titleFor(outcome: Outcome, distanceM: number, oppScoreM: number): string {
    const roll = mulberry32(this.runSeed);
    const state = this.playerSim.state;
    const oppEndFrame =
      this.ctx.opponent.deathFrame >= 0 ? this.ctx.opponent.deathFrame : MAX_FRAMES;
    const deathFrame = state.dead ? state.deathFrame : MAX_FRAMES;
    const seconds = Math.max(0.1, (oppEndFrame - deathFrame) / 60);
    const params = {
      name: this.ctx.opponent.nickname,
      m: distanceM,
      n: oppScoreM,
      d: Math.abs(distanceM - oppScoreM),
      s: seconds.toFixed(1),
    };

    // §4bis.31: a death that can be handed on gets the gift voice, because the
    // screen's primary action is a gift. `pool.win`'s 「你从 {name} 手里多抢了
    // {d} 米」 is the exact line this rules out.
    // §4bis.31: the stamp speaks in the voice of the action this screen offers.
    // Only the POOL changes — same stream, same draw position — so this is a
    // copy change and not a timing one.
    const gift = this.handoffOffered(distanceM);
    const hasParent = this.ctx.parentRunId !== undefined;
    const base =
      this.ctx.entryType === 'rerun'
        ? 'pool.rerun'
        : outcome === 'tie'
          ? 'pool.tie'
          : outcome === 'win'
            ? hasParent
              ? 'pool.win'
              : 'pool.defaultWin'
            : hasParent
              ? 'pool.lose'
              : 'pool.defaultLose';
    return pick(deathStampPool(gift, base) as PoolKey, roll, params);
  }

  // --- result screen ------------------------------------------------------

  private showResult(distanceM: number, finished: boolean): void {
    if (this.ctx.duel && this.duelOutcome) {
      this.showDuelResult(this.ctx.duel, this.duelOutcome, distanceM);
      return;
    }
    const outcome = this.localOutcome(distanceM);
    const oppScoreM = this.ctx.opponent.scoreM;
    const hasParent = this.ctx.parentRunId !== undefined;

    // Personal best per track — cold-read finding: without a yardstick, "is
    // 1558 m good?" has no answer. The number is a CACHE of a record the server
    // holds (src/best.ts, H02 B4); the run just played is written into it only
    // once the server verifies it, and `loadLadder` repaints these lines if the
    // server turns out not to back what we remembered.
    const tail: string[] = [];
    if (finished) tail.push(t('result.finished'));
    else if (outcome === 'lose') {
      const oppEndFrame =
        this.ctx.opponent.deathFrame >= 0 ? this.ctx.opponent.deathFrame : MAX_FRAMES;
      const state = this.playerSim.state;
      const deathFrame = state.dead ? state.deathFrame : MAX_FRAMES;
      const seconds = Math.max(0.1, (oppEndFrame - deathFrame) / 60);
      tail.push(t('result.deltaSeconds', { s: seconds.toFixed(1) }));
    }
    // §4bis.31: on a full run the 帮我 button is the FIRST time this player has
    // met the second verb, and its label states a gift without explaining the
    // mechanism. One line does that — what the friend actually receives — so
    // the ask is legible rather than merely warm.
    if (this.handoffOffered(distanceM)) tail.push(t('handoff.ctaOwnSub'));
    // `scoreM` is passed rather than closed over: the repaint happens after the
    // submit, where the verified score rules (`res.scoreM || localDistanceM`).
    this.rebuildLines = (prevBestM: number, scoreM: number): string[] => {
      const best = bestLine(prevBestM, scoreM);
      return [
        t('result.opponentLine', { name: this.ctx.opponent.nickname, n: oppScoreM }),
        ...(best ? [t(best.key, { n: best.n })] : []),
        ...tail,
      ];
    };
    const lines = this.rebuildLines(readBest(this.ctx.track.id), distanceM);

    const share = (label: string): ResultButton => ({
      label,
      needsShareCode: true,
      onClick: () => void this.doShare(distanceM),
    });
    const again: ResultButton = { label: t('btn.retry'), onClick: () => this.restart() };

    // Design §4.4's base arrangement, expressed as SLOT KINDS so §4bis.31's
    // precedence and §4bis.21's ladder rule can be resolved by one pure
    // function (`resultSlots`) instead of by two rules editing the same two
    // variables — which is how the retry button once vanished when both flags
    // were on at once.
    let baseKinds: { basePrimary: SlotKind; baseSecondary: SlotKind | null };
    const labels: Partial<Record<SlotKind, ResultButton>> = { again };
    if (this.ctx.entryType === 'rerun' || !hasParent) {
      labels.share = share(t('btn.findSomeone'));
      baseKinds = { basePrimary: 'share', baseSecondary: 'again' };
    } else if (outcome === 'win') {
      labels.share = share(t('btn.sendBack'));
      labels.shareAlt = share(t('btn.challengeOthers'));
      baseKinds = { basePrimary: 'share', baseSecondary: 'shareAlt' };
    } else if (outcome === 'tie') {
      labels.share = share(t('btn.judge'));
      baseKinds = { basePrimary: 'again', baseSecondary: 'share' };
    } else {
      labels.share = share(t('btn.passOn'));
      baseKinds = { basePrimary: 'again', baseSecondary: 'share' };
    }

    // Design §4.4 keeps primary/secondary fixed; extras are low-emphasis.
    // Cold-read finding: winners want to push the score higher and the win
    // rows had no retry — add it wherever primary/secondary don't include it.
    // With the ladder on, that extra 再来一次 is dropped: rung 1 IS a rematch
    // against the same opponent, and two controls for one action reads as two
    // different actions.
    // §4bis.31 — the second verb on a FULL run's death, which is the scope
    // decision that makes 帮我 fire on ~40% of deaths instead of only on duel
    // wins. Precedence: eligible ⇒ 帮我 primary, the challenge share demoted to
    // secondary. The share ask keeps the count it had (001's finding); what
    // changes is which verb leads.
    labels.handoff = {
      label: this.handoffCtaLabel(distanceM),
      needsShareCode: true,
      onClick: () => void this.doShare(distanceM, 'result', 'handoff'),
    };
    // False in production at mount (the server has not answered), true only
    // under `&handoffmint=1`. `applySubmitted` promotes the real case.
    const offerAtMount = this.handoffOffered(distanceM);
    this.handoffShown = offerAtMount;
    const plan = resultSlots({
      ...baseKinds,
      ladder: this.ctx.ladder,
      handoff: offerAtMount,
    });
    const primary = labels[plan.primary]!;
    const secondary = plan.secondary === null ? null : (labels[plan.secondary] ?? null);
    const extras: ResultButton[] = plan.extras
      .map((k) => labels[k])
      .filter((b): b is ResultButton => b !== undefined);
    if (this.ctx.entryType === 'link') {
      extras.push({
        label: t('btn.ownChain'),
        needsShareCode: true,
        onClick: () => this.startOwnChain(),
      });
    }

    this.result = new ResultView(this.deps.overlay, {
      title: this.titleFor(outcome, distanceM, oppScoreM),
      distanceM,
      metersLabel: t('hud.meters'),
      lines,
      primary,
      secondary,
      extras,
      showChain: hasParent,
      chain: null,
      chainEmptyText: t('chain.empty'),
      onTutorialAgain: () => armTutorialNext(),
      // Rung 1 needs no server, so the ladder appears with the screen and the
      // other two rungs fill in; waiting for the network would make the section
      // pop in under the player's thumb. Rung 2 is *absent* until the answer
      // lands (`selfKnown` defaults false): since B3 its empty state asserts
      // that the server holds nothing of yours, which is untrue on most screens
      // and unknowable before the request returns.
      ladder: this.ctx.ladder
        ? { title: t('ladder.title'), rows: this.buildLadder(distanceM, null, null).rows }
        : null,
    });
    if (__BEATME_DEBUG__) this.play?.scene('result');
    // Exactly once per game_end: on a peak run the seal already fired this at
    // its mount (§4bis.22). The coverage prop ships on every run, both arms.
    if (!this.resultViewSent) {
      this.resultViewSent = true;
      trackEvent('result_view', {
        entryType: this.ctx.entryType,
        distanceM,
        outcome,
        peak: this.peakCoverage,
        peakArm: this.peakArm(),
        duelArm: this.duelArm(),
        duel: 0,
        handoffArm: this.handoffArm(),
        handoff: 0,
      });
    }
  }

  /**
   * The duel result screen. Deliberately NOT the full-run screen with a
   * different title: nothing here is a score. There is no personal-best line
   * (a duel never touches the cache), no chain board (a duel never ranks), and
   * no ladder (its rungs are full-run rivals).
   *
   * The primary action is the whole iteration in one button: 回敬 on a sealed
   * outcome, an instant retry on a start-line accident.
   */
  private showDuelResult(duel: DuelSlice, outcome: DuelOutcome, distanceM: number): void {
    const params = this.duelParams(duel, distanceM);
    const roll = mulberry32(this.runSeed);
    // §4bis.31: the stamp speaks in the voice of the action the screen offers.
    // Same seeded stream, same draw position — only the POOL changes — so
    // baselines stay deterministic and this is a copy change, not a timing one.
    const giftVoice = this.handoffOffered(distanceM) && outcome !== 'instant';
    const title =
      outcome === 'instant'
        ? pick('pool.duelInstant', roll, params)
        : pick(deathStampPool(giftVoice, duelStampPool(outcome)) as PoolKey, roll, params);
    // §4bis.31 §5 — **whose score is this? Nobody's, and that is the point.**
    // A hand-back's stream is almost entirely other people's play, so the
    // screen says 「这条命跑到 {p} 米」 and never 「你跑了 {p} 米」; the number
    // that is YOURS is the margin, which the CTA carries. On a challenge this
    // is the ordinary 12 秒快斗 receipt, unchanged.
    const handoffRun = isHandoff(duel);
    const finishedRun = this.playerSim.state.finished;
    const lines = [
      handoffRun ? t('handoff.label') : t('duel.label'),
      ...(handoffRun ? [t('handoff.life', { p: distanceM })] : []),
      pick(handoffRun ? 'pool.handoffResult' : duelLinePool(outcome), roll, params),
      // `handoff.ctaSub` 「他从你倒下的地方接着跑」 was CUT here, not moved again.
      // The duel result screen is the dense one — label, 「这条命跑到 {p} 米」,
      // the receipt line, sometimes the finish line, three buttons and the
      // nickname editor — and the CTA directly beneath already says
      // 「交回给 {name} · 你加了 {n} 米」, which states the person, the action
      // and the number. A sub-line repeating it bought nothing and cost a row
      // on the screen where a row costs the nickname field. The full-run
      // screen, which is sparser and whose CTA names nobody, keeps its
      // `handoff.ctaOwnSub`.
      // The terminal state of a lineage, and the only place it can be said: the
      // volley ENDS at the finish (`handoffEligible` refuses a finished run),
      // so a life that gets there is the strongest thing this feature produces.
      ...(handoffRun && finishedRun
        ? [
            t('handoff.finish', {
              k: this.ctx.handoff?.contributors?.length ?? 2,
              p: distanceM,
            }),
          ]
        : []),
    ];

    const retry: ResultButton = { label: t('duel.retry'), onClick: () => this.retryDuel() };
    const again: ResultButton = { label: t('btn.retry'), onClick: () => this.retryDuel() };
    const sendBack: ResultButton = {
      label: pick('pool.duelCta', roll, params),
      needsShareCode: true,
      onClick: () => void this.doShare(distanceM, 'duel'),
    };
    // 跑全程 — the secondary the entry screen also offers. Full runs stay the
    // score canon and are what mint every slice, so the way back to one is
    // never more than a tap away.
    const full: ResultButton = { label: t('duel.full'), onClick: () => this.leaveDuel() };

    // §4bis.31 — the second verb, at the moment it is finally worth something.
    // Named a PERSON, and stating a GIFT: the label says what you DID (+{n} m),
    // never what you failed at. That is the whole reason 帮我 reads as trust
    // where 打败我 reads as a boast — a boast needs a peak, a gift only needs
    // you to have gone further than the person before you.
    // §4bis.31: whether this run can be handed back is a SERVER derivation, and
    // in production it has not answered yet — so the screen mounts with the verb
    // it can honestly offer and `applySubmitted` promotes the other one if the
    // answer allows. Mounting 帮我 and withdrawing it would show an offer the
    // link cannot keep, which is the failure this whole change removes.
    //
    // `handoffOffered` is nonetheless consulted here, because a debug build can
    // force the answer (`&handoffmint=1`) — autoplay never submits, so without
    // that path this verb could not be photographed at all.
    const offerHandoff = this.handoffOffered(distanceM);
    this.handoffShown = offerHandoff;
    const handBack: ResultButton = {
      label: this.handoffCtaLabel(distanceM),
      needsShareCode: true,
      onClick: () => void this.doShare(distanceM, 'duel', 'handoff'),
    };
    this.result = new ResultView(this.deps.overlay, {
      title,
      distanceM,
      metersLabel: t('hud.meters'),
      lines,
      primary: outcome === 'instant' ? retry : offerHandoff ? handBack : sendBack,
      secondary: outcome === 'instant' ? full : offerHandoff ? sendBack : again,
      extras: outcome === 'instant' ? [] : offerHandoff ? [again, full] : [full],
      showChain: false,
      chain: null,
      chainEmptyText: t('chain.empty'),
      onTutorialAgain: () => armTutorialNext(),
      ladder: null,
    });
    if (__BEATME_DEBUG__) this.play?.scene('result');
    if (!this.resultViewSent) {
      this.resultViewSent = true;
      trackEvent('result_view', {
        entryType: this.ctx.entryType,
        distanceM,
        outcome: this.localOutcome(distanceM),
        peak: this.peakCoverage,
        peakArm: this.peakArm(),
        duelArm: this.duelArm(),
        duel: 1,
        duelOutcome: outcome,
        handoffArm: this.handoffArm(),
        handoff: isHandoff(duel) ? 1 : 0,
      });
    }
  }

  /**
   * §4bis.31: the offer itself, fired WITH the death screen and never as
   * `result_view` (which is contractually once per `game_end`, §4bis.22).
   *
   * It carries both numbers the two screens separate: `marginM` is what YOU
   * added — the personal superlative, always positive — and `frontierM` is
   * where the life now stands, which is nobody's score. Fired on every eligible
   * death in the arm, offered or not, so T2's denominator is the deaths where
   * the verb COULD have been chosen rather than the ones where it was.
   */
  private noteHandoffOffer(distanceM: number, offered: boolean): void {
    if (this.ctx.handoffArm !== true) return;
    // Exactly once per `game_end`, like `result_view`. Two surfaces can make
    // the ask — the seal (which leads on a sealed duel) and the result screen
    // behind it — and T1 reads offers as its denominator, so counting the same
    // ask twice would deflate the return rate by exactly the share of runs that
    // sealed. Fired from BOTH so neither path can silently drop it.
    if (this.handoffOfferSent) return;
    this.handoffOfferSent = true;
    trackEvent('handoff_offer', {
      marginM: this.handoffAddedM(distanceM),
      frontierM: distanceM,
      offered: offered ? 1 : 0,
      handoff: isHandoff(this.ctx.duel) ? 1 : 0,
      handoffArm: 1,
    });
  }

  /** 跑全程 — drop the slice and run the whole track against the same ghost.
   *  The link's secondary action, and the severance path if duels ever have to
   *  be collapsed (design §Risks: "collapse to duel-only" is the other way). */
  private leaveDuel(): void {
    this.ctx = { ...this.ctx, duel: null };
    this.duelAttempt = 0;
    this.result?.remove();
    this.result = null;
    this.shareCode = null;
    this.reset();
    this.render(0);
    this.beginRun();
  }

  // --- rival ladder (§4bis.21) ---------------------------------------------

  /** The rungs this screen offers, and the same rungs as localized rows with
   *  their rematch handlers attached. */
  private buildLadder(
    distanceM: number,
    selfBest: Opponent | null,
    daily: Opponent | null,
    thisRunId?: string,
    opts: { selfKnown?: boolean } = {},
  ): { rungs: Rung[]; rows: LadderRow[] } {
    const rungs = ladderRungs({
      opponent: { nickname: this.ctx.opponent.nickname, scoreM: this.ctx.opponent.scoreM },
      playerScoreM: distanceM,
      selfBest: selfBest ? { nickname: t('lane.weeklyYou'), scoreM: selfBest.scoreM } : null,
      selfBestIsThisRun: selfBest !== null && thisRunId !== undefined && selfBest.runId === thisRunId,
      selfKnown: opts.selfKnown ?? false,
      daily: daily ? { nickname: daily.nickname, scoreM: daily.scoreM } : null,
    });
    return { rungs, rows: rungs.map((rung) => this.ladderRow(rung, selfBest, daily)) };
  }

  private ladderRow(rung: Rung, selfBest: Opponent | null, daily: Opponent | null): LadderRow {
    const gap = rung.gapM === null ? null : t('ladder.gap', { d: rung.gapM });
    if (rung.id === 'opponent') {
      return {
        id: rung.id,
        title: t('ladder.rung.opponent'),
        sub: t('ladder.line', { name: rung.nickname ?? '', n: rung.scoreM ?? 0 }),
        // No gap left means you are ahead of them: the offer is to do it again,
        // not to make up 0 metres.
        cta: gap ?? t('ladder.again'),
        onClick: () => this.takeRung(rung),
      };
    }
    if (rung.id === 'self') {
      if (!rung.clickable || !selfBest) {
        return {
          id: rung.id,
          title: t('ladder.rung.self'),
          sub: t('ladder.selfEmpty'),
          cta: null,
          disabled: true,
        };
      }
      return {
        id: rung.id,
        title: t('ladder.rung.self'),
        // A record set seconds ago says so; anything else is the week's best.
        sub: rung.justSet
          ? t('ladder.selfJustSet', { n: rung.scoreM ?? 0 })
          : t('ladder.score', { n: rung.scoreM ?? 0 }),
        // 「再赢一次」 is rung 1's copy — you never *beat* yourself, so level
        // with your own record the offer is to go past it.
        cta: gap ?? t('ladder.selfBeat'),
        onClick: () => this.takeRung(rung, selfBest),
      };
    }
    return {
      id: rung.id,
      title: t('ladder.rung.daily'),
      sub: t('ladder.line', { name: rung.nickname ?? '', n: rung.scoreM ?? 0 }),
      cta: gap ?? t('ladder.again'),
      onClick: () => this.takeRung(rung, daily),
    };
  }

  /** One tap on a rung = one rematch. No rewards anywhere on this path. */
  private takeRung(rung: Rung, ghost?: Opponent | null): void {
    trackEvent('ladder_click', { rung: rung.id, gapM: rung.gapM ?? 0 });
    if (rung.id === 'opponent') {
      this.restart();
      return;
    }
    if (!ghost) return;
    if (rung.id === 'self') {
      // Your own stream, raced as an opponent — the same client-side path
      // start-your-own-chain uses. Labelled 本周的你, never your nickname: an
      // identically-named ghost reads as a stranger (phone playtest).
      this.raceRival({ ...ghost, nickname: t('lane.weeklyYou') }, t('ladder.selfBanner'));
      return;
    }
    this.raceRival(ghost, t('ladder.dailyBanner', { name: ghost.nickname }));
  }

  /**
   * Race an opponent fetched for the ladder. Always a walk-in with no parent:
   * a ladder rematch must not extend the chain the player was in, or a private
   * self-race would show up as someone else's challenger.
   */
  private raceRival(opponent: Opponent, banner: string | null): void {
    const { parentRunId: _parent, ...rest } = this.ctx;
    this.ctx = { ...rest, entryType: 'walkin', opponent, firstEver: false };
    this.rerunBanner = banner;
    this.rerun();
  }

  /**
   * Fetch rungs 2 and 3 and refresh the ladder. Called once per result screen,
   * only for devices in the arm. Either request failing just costs that rung —
   * the ladder is dressing on a screen that already works.
   */
  private async loadLadder(distanceM: number, thisRunId?: string): Promise<void> {
    if (!this.ctx.ladder || this.ladderLoaded) return;
    this.ladderLoaded = true;
    const trackId = this.ctx.track.id;
    // The screen these rungs belong to. A player who taps a rung while the
    // requests are still in flight starts a whole new run; when the stale
    // answers land, they must not repaint the new run's ladder with the old
    // run's gaps.
    const view = this.result;
    // `undefined` = the request failed (it proves nothing); `null` = the server
    // answered, and holds no eligible run of ours.
    const [answer, daily] = await Promise.all([
      fetchSelfBest(trackId).catch(() => undefined),
      fetchDailyRival(trackId).catch(() => null),
    ]);
    if (!view || this.result !== view) return;
    const selfBest = answer ?? null;
    // B4: this answer IS the server's record for this track. A remembered best
    // it cannot back (a wiped world, a rejected submit, a run the rung's
    // eligibility excludes) stops being claimed here rather than contradicting
    // the rung right underneath it. A demo run never touches the cache.
    if (answer !== undefined && !(__BEATME_DEBUG__ && this.play?.isAutoplay === true)) {
      const corrected = reconcileBest(trackId, selfBest ? selfBest.scoreM : null);
      if (corrected !== null && this.rebuildLines) {
        view.setLines(this.rebuildLines(corrected, distanceM));
      }
    }
    const { rungs, rows } = this.buildLadder(distanceM, selfBest, daily, thisRunId, {
      // A request that never answered leaves rung 2 unknown, not empty: since
      // B3 the empty state asserts "the server holds no run of yours", and we
      // must not assert that on a failure — or, at mount, on no evidence.
      selfKnown: answer !== undefined,
    });
    view.setLadder(rows);
    trackEvent('ladder_shown', {
      entryType: this.ctx.entryType,
      rungs: rungsAvailable(rungs),
    });
  }

  private async doShare(
    distanceM: number,
    moment: 'peak' | 'result' | 'duel' = 'result',
    /**
     * §4bis.31: which verb this share act carries. **The verb rides the LINK,
     * not the run** — one run can be offered either way, which is what makes it
     * a property of the SHARE and the shape the platform pivot needs.
     */
    verb: 'challenge' | 'handoff' = 'challenge',
  ): Promise<void> {
    if (!this.shareCode) return;
    // §4bis.20: the link carries this device's card arm so the open can be split
    // by it. The share TEXT is identical in both arms — one variable per
    // experiment — and the card itself is chosen server-side from the same rule.
    const deviceId = ensureDeviceId();
    const variant = cardVariantFor(deviceId);
    // §4bis.28: a duel link carries `?d=1` and deliberately OMITS `?v=`, so the
    // open side of the card A/B (§4bis.20) ignores it entirely — unknown/absent
    // variants are already ignored there — and 001's experiment keeps a clean
    // denominator while duels are ramping.
    const base = shareUrlFor(location.origin, this.shareCode, deviceId);
    // §4bis.31: `?h=1` mirrors `?d=1` and, like it, omits `?v=`. A hand-back
    // can be minted off a FULL run too (「any death past 1200 f」), so this is
    // deliberately not nested inside the `this.ctx.duel` branch.
    const url =
      verb === 'handoff'
        ? `${location.origin}/r/${this.shareCode}?h=1`
        : this.ctx.duel
          ? `${location.origin}/r/${this.shareCode}?d=1`
          : base;
    const roll = mulberry32(this.runSeed ^ 0x9e3779b9);
    // §4bis.31: the hand-back card, and the same split the CTA makes. A duel
    // hand-back has a predecessor to name and a grave to cite; a FULL RUN's
    // hand-back has neither, and the duel pool would have rendered
    // 「刚才的你 死在 0 米」 — naming the ghost-lane label as a person and
    // citing a source run that does not exist. That text goes verbatim into a
    // chat app, so the two cases get two pools.
    const text =
      verb === 'handoff'
        ? this.ctx.duel
          ? pick('pool.handoffCard', roll, {
              name: this.ctx.opponent.nickname,
              d: this.ctx.duel.sourceEndM,
              p: distanceM,
              n: this.handoffAddedM(distanceM),
            })
          : // A full run started this life, so what you ADDED is all of it:
            // {n} and {p} are the same number, and both are supplied so the
            // pool can be written on the same rule as its sibling.
            pick('pool.handoffCardOwn', roll, {
              p: distanceM,
              n: this.handoffAddedM(distanceM),
            })
        : pick('pool.shareText', roll, { m: distanceM, name: this.ctx.opponent.nickname });
    const channel = await shareRun(this.deps.overlay, {
      url,
      text,
      title: t('app.title'),
    });
    // §4bis.22: `moment` says which surface asked (seal vs result screen);
    // payload, variant, channel and entryType are untouched (§4bis.20).
    trackEvent('share_click', {
      channel,
      entryType: this.ctx.entryType,
      // A duel link carries no `?v=`, so it reports no variant either: the
      // share side of the card A/B must not see a run it never split.
      ...(this.ctx.duel || verb === 'handoff' ? {} : { variant }),
      moment,
      // §4bis.31: T2 reads this — among eligible deaths IN ARM, what share of
      // share clicks chose 帮我. Reported on every share, both verbs, or the
      // denominator would only contain the verb we hoped for.
      verb,
      peakArm: this.peakArm(),
      duelArm: this.duelArm(),
      duel: this.ctx.duel ? 1 : 0,
      handoffArm: this.handoffArm(),
      handoff: isHandoff(this.ctx.duel) ? 1 : 0,
    });
  }

  /** 再来一次 — same track, same opponent, fresh run (design §4.4). */
  private restart(): void {
    this.ctx = { ...this.ctx, entryType: 'rerun', firstEver: false };
    this.rerun();
  }

  /** 开一条自己的链 — same track, but the ghost is the run that just ended and
   *  the new run has no parent, so sharing it roots a chain at the player.
   *  The opponent is assembled locally: the verified run id and nickname from
   *  the submit response plus the input stream we recorded this run. */
  private startOwnChain(): void {
    const res = this.submitted;
    if (!res) return;
    const opponent: Opponent = {
      runId: res.runId,
      seed: this.ctx.track.seed,
      inputs: this.inputs.slice(),
      // Racing your own previous run: label it 刚才的你, not your nickname —
      // phone playtests read the identically-named ghost as "someone else".
      nickname: t('lane.pastYou'),
      scoreM: res.scoreM,
      deathFrame: res.deathFrame,
      finished: res.finished,
      revives: this.revives,
    };
    // Drop parentRunId entirely — a walk-in with a parent would extend the
    // chain we are trying to leave.
    this.raceRival(opponent, t('ownchain.banner'));
  }

  /** Tear the result screen down and start another run on the current ctx. */
  private rerun(): void {
    this.result?.remove();
    this.result = null;
    this.shareCode = null;
    this.reset();
    this.render(0);
    this.beginRun();
  }
}

// --- helpers --------------------------------------------------------------

function blankActor(): ActorSnapshot {
  return { x: 0, y: 0, sliding: false, grounded: true, dead: false };
}

function snapshot(sim: Sim): ActorSnapshot {
  const s = sim.state;
  return { x: s.x, y: s.y, sliding: s.sliding, grounded: s.grounded, dead: s.dead };
}

/** Opponent streams come off the wire; keep them well-formed before replay. */
function sanitizeGhostInputs(raw: InputEvent[] | undefined): InputEvent[] {
  if (!raw || !Array.isArray(raw)) return [];
  const out: InputEvent[] = [];
  let last = 0;
  for (const ev of raw) {
    if (!ev || typeof ev.f !== 'number' || typeof ev.a !== 'number') continue;
    const f = Math.max(last, Math.min(MAX_FRAMES, Math.floor(ev.f)));
    out.push({ f, a: ev.a });
    last = f;
    if (out.length >= 600) break;
  }
  return out;
}
